<template>
  <EditorModal
    :toolbar-right-buttons="toolbarRightButtons"
    :content-header-toolbar-start="contentHeaderButtonsLeft"
    :title="selectedTemplateName"
    :subtitle="subtitle"
    :tabs="tabs"
    class="template-editor"
    :green-header="!!selectedSyncElement"
  >
    <template
      v-if="isLeftToolbarVisible"
      #sidebar
    >
      <InformationPanel
        v-if="state.leftToolbar.show.informationPanel && !state.leftToolbar.panelLoading"
        :form-validation="informationPanelValidation"
      />
      <WidgetsPanel
        v-if="state.leftToolbar.show.widgetsPanel && !state.leftToolbar.panelLoading"
        :is-closable="isWidgetPanelClosable"
      />
      <LoadElementsPanel
        v-if="state.leftToolbar.show.loadElementsPanel && !state.leftToolbar.panelLoading"
      />
      <ConfigurationPanel
        v-if="(state.leftToolbar.show.configurationPanel || state.leftToolbar.show.designPanel) && selectedStructure && !state.leftToolbar.panelLoading"
        :key="selectedStructure.identifier"
        :structure-config="selectedStructure.config"
        :parser-values="selectedStructure.parserValues"
        :is-closable="isConfigurationPanelClosable"
        :structure-actions="selectedStructureActions"
      />
      <TranslationPanel
        v-if="state.leftToolbar.show.translationPanel && !state.leftToolbar.panelLoading"
        :is-closable="isTranslationPanelClosable"
      />
      <HistoryPanel
        v-if="state.leftToolbar.show.historyPanel && !state.leftToolbar.panelLoading"
        :history-list="historyList"
        :selected-history="selectedHistory"
        :has-more="historyHasMore"
        :is-loading-more="historyIsLoadingMore"
        :total-items="historyTotalItems"
        @on-change-history="handleChangeHistory"
        @load-more="handleLoadMoreHistory"
      />
      <CssPanel
        v-if="state.leftToolbar.show.cssPanel && !state.leftToolbar.panelLoading"
      />
      <CssPanelMediaQueries
        v-if="state.leftToolbar.show.cssPanelMediaQueries && !state.leftToolbar.panelLoading"
      />
      <Loader
        v-if="(state.leftToolbar.show.configurationPanel) && !selectedStructure"
      />
      <Loader
        v-if="state.leftToolbar.panelLoading"
      />
      <Loader
        v-if="state.leftToolbar.historyRestoreProgress.isRestoring && state.leftToolbar.show.historyPanel"
      />
    </template>
    <template #content>
      <HistoryRestoreLoader
        v-if="state.leftToolbar.historyRestoreProgress.isRestoring"
        :current="state.leftToolbar.historyRestoreProgress.current"
        :total="state.leftToolbar.historyRestoreProgress.total"
        :action-detail="state.leftToolbar.historyRestoreProgress.actionDetail"
      />
      <TemplateLiveView
        :key="livePreviewKey"
        :template-id="selectedTemplateId"
        :template-type="selectedTemplateType"
        :width="width"
        @on-iframe-loaded="iframeLoaded"
      />
    </template>
  </EditorModal>
  <InputTextModal
    :display-dialog="displayRenameTemplateModal"
    :display-cancel-button="true"
    :input-value="valueRenameTemplateModal"
    header="templateBuilder.modals.renameTemplate"
    @on-close-dialog="displayRenameTemplateModal=false"
    @on-validate="(emitParameters) => rename(selectedTemplateId, emitParameters)"
  />
  <SaveElementModal
    v-if="displaySaveElementModal"
    ref="saveElementModalRef"
    :display-dialog="displaySaveElementModal"
    :display-cancel-button="true"
    :close-on-validate="false"
    :element-name="nameSaveElementModal"
    :element-sync="syncStateSaveElementModal"
    :element-type="typeSaveElementModal"
    :show-loading="true"
    @on-close-dialog="handleSaveElementClose"
    @on-validate="(emitParameters, done) => saveElement(emitParameters, done)"
    @on-search-elements="handleSearchElements"
  />
  <SearchElementsModal
    v-if="displaySearchElementsModal"
    :display-dialog="displaySearchElementsModal"
    :element-type="searchElementsType"
    :element-part-type="searchElementsPartType"
    :element-subtype="searchElementsSubtype"
    :include-all-elements-part-type="includeAllElementsPartType"
    @on-close-dialog="handleSearchElementsClose"
    @on-element-selected="handleSearchElementSelected"
  />
  <SendTestModal
    v-if="isSendTestModalVisible"
    @on-validate="(emitParameters) => sendTestTemplate(emitParameters)"
  />
  <ColumnWidgetModal
    v-if="state.isColumnWidgetModalVisible"
  />
  <LoadElementsModal
    v-if="displayElementsModal"
    @insert-element="handleInsertElement"
  />
  <ConfirmDialog group="saveElement">
    <template #message="slotProps">
      <div class="flex flex-column w-full gap-3">
        <p>{{ slotProps.message.message }}</p>
        <div
          v-if="toEraseElementId"
          class="to-erase-section"
          :style="{
            backgroundImage: `url(${MEDIA_URL_PART_THUMB_GENERATION.replace('{SHA1_KEY}', generateSha1Hash(toEraseElementId))}&t=${new Date().getTime()})`,
          }"
        />
        <SyncElementUsedTemplates
          v-if="toEraseElementSyncId"
          :id-shop="idShop"
          :sync-element-id="toEraseElementSyncId"
          @loaded="handleSyncElementUsedTemplatesLoaded"
        >
          <template #description>
            <div
              v-if="!syncElementUsedTemplatesLoaded"
              class="flex gap-1 align-items-center"
            >
              <ProgressSpinner
                style="width: 25px; height: 25px"
                stroke-width="8"
                fill="var(--surface-ground)"
                animation-duration="1.0s"
                aria-label="Custom ProgressSpinner"
              />
            </div>
            <p
              v-else
            >
              <b>{{ getSyncElementUsedTemplatesNumberTitleWhenSaving(!toEraseIsNewSync) }}</b>
            </p>
          </template>
        </SyncElementUsedTemplates>
      </div>
    </template>
  </ConfirmDialog>
  <ConfirmDialog group="syncedElements" />
  <ConfirmDialog group="hasSyncElements" />
  <ConfirmDialog group="leaveWithoutSaveConfirmDialog" />
  <ConfirmDialog group="confirmationModal" />
  <ConfirmDialog group="versionConflict" contentClass="version-conflict-dialog-content">
    <template #message>
      <div class="flex flex-column w-full gap-3">
        <p>{{ currentConflictMessage }}</p>
        <div class="flex flex-column gap-2 p-3 surface-100 border-round">
          <div class="flex align-items-center gap-2">
            <i class="far fa-clock text-primary" />
            <strong>{{ t('templateBuilder.confirm.versionConflict.localVersion') }}:</strong>
            <span>{{ currentConflictLocalDate }}</span>
          </div>
          <div class="flex align-items-center gap-2">
            <i class="far fa-database text-green-500" />
            <strong>{{ t('templateBuilder.confirm.versionConflict.remoteVersion') }}:</strong>
            <span>{{ currentConflictRemoteDate }}</span>
          </div>
        </div>
        <p class="text-sm text-color-secondary">
          <i class="far fa-exclamation-triangle text-orange-500 mr-2" />
          {{ t('templateBuilder.confirm.versionConflict.explanation') }}
        </p>
      </div>
    </template>
  </ConfirmDialog>
  <DuplicateModal
    v-if="showDuplicationModal"
    :translation="t"
    :list-name="newListName"
    icon="far fa-exclamation-triangle"
    :message="t('campaigns.bulk.list.actions.duplicate.message')"
    @on-close-modal="closeDuplicationModal"
    @on-execute="duplicateConfirmationDialog"
  />
  <TemplateSelector
    v-if="showTemplateSelector"
    type-templates="none"
    origin-call="myTemplates"
    :id-campaign="null"
    :duplicate="true"
    @on-choose-template="selectTemplateFromTemplateSelector"
    @on-close-dialog="closeTemplateSelector"
  />
  <ChooseZipFileModal
    v-if="isChooseZipFileModalVisible"
  />
  <Dialog
    v-if="displaySyncElementTemplatesModal"
    v-model:visible="displaySyncElementTemplatesModal"
    modal
    :header="t('templateBuilder.saveSyncElementModal.previewAllInstancesTitle')"
    :style="{ width: '50rem' }"
  >
    <SyncElementUsedTemplates
      v-if="selectedSyncElement"
      :id-shop="idShop"
      :sync-element-id="selectedSyncElement.syncElementId ?? ''"
    >
      <template #description>
        <p>{{ syncElementUsedTemplateListLabel }}</p>
      </template>
    </SyncElementUsedTemplates>
    <template #footer>
      <Button
        :label="t('close')"
        class="p-button p-button-secondary"
        @click="displaySyncElementTemplatesModal = false"
      />
    </template>
  </Dialog>
  <Dialog
    v-if="displaySaveSyncElementTemplatesModal"
    v-model:visible="displaySaveSyncElementTemplatesModal"
    modal
    :header="t('templateBuilder.saveSyncElementModal.saveForAllInstancesTitle')"
    :style="{ width: '75rem' }"
    @hide="handleSaveSyncElementTemplatesModalHide"
  >
    <div class="relative">
      <SyncElementUsedTemplates
        v-if="selectedSyncElement"
        :id-shop="idShop"
        :sync-element-id="selectedSyncElement.syncElementId ?? ''"
        @loaded="handleSyncElementUsedTemplatesLoaded"
      >
        <template #description>
          <div
            v-if="!syncElementUsedTemplatesLoaded"
            class="flex gap-1 align-items-center"
          >
            <ProgressSpinner
              style="width: 25px; height: 25px"
              stroke-width="8"
              fill="var(--surface-ground)"
              animation-duration="1.0s"
              aria-label="Custom ProgressSpinner"
            />
          </div>
          <p
            v-else
          >
            <b>{{ getSyncElementUsedTemplatesNumberTitle(false) }}</b>
          </p>
          <p>{{ syncElementUsedTemplateListLabel }}</p>
        </template>
      </SyncElementUsedTemplates>

      <div v-if="shouldShowNotUsedTemplates">
        <Divider class="sync-element-divider" />
        <SyncElementNotUsedTemplates
          ref="syncElementNotUsedTemplatesRef"
          :id-shop="idShop"
          :sync-element-id="selectedSyncElement?.syncElementId ?? ''"
          :template-type="selectedTemplateType"
          :element-part-type-or-subtype="selectedSyncElement?.elementSubtype || selectedSyncElement?.elementType || ''"
          :is-part-type="selectedSyncElement?.elementPartType !== undefined && selectedSyncElement?.elementPartType !== null ? true : false"
          :exclude-ids="selectedTemplateId ? [selectedTemplateId] : []"
          @loaded="handleSyncElementNotUsedTemplatesLoaded"
          @selection-changed="handleNotUsedTemplatesSelectionChanged"
        >
          <template #description>
            <h4>{{ t('templateBuilder.saveSyncElementModal.notUsedTemplatesTitle') }}</h4>
            <p>{{ syncElementNotUsedTemplatesDescription }}</p>
          </template>
        </SyncElementNotUsedTemplates>
      </div>

      <!-- Loader overlay -->
      <div v-if="saveForAllInstancesLoading">
        <Loader />
      </div>
    </div>
    <template #footer>
      <Button
        :label="t('cancel')"
        class="p-button p-button-secondary"
        :disabled="saveForAllInstancesLoading"
        @click="displaySaveSyncElementTemplatesModal = false"
      />
      <Button
        class="p-button p-button-primary"
        :label="t('save')"
        :disabled="saveForAllInstancesLoading"
        @click="handleSaveForAllInstances"
      />
    </template>
  </Dialog>
  <Dialog
    v-if="displayUnlinkSyncElementModal"
    v-model:visible="displayUnlinkSyncElementModal"
    modal
    :header="t('templateBuilder.editSyncElementActions.unlinkAndSave')"
    :style="{ width: '75rem' }"
    @hide="handleUnlinkSyncElementModalHide"
  >
    <div class="relative">
      <SyncElementUsedTemplates
        v-if="selectedSyncElement"
        ref="syncElementUsedTemplatesRef"
        :id-shop="idShop"
        :sync-element-id="selectedSyncElement.syncElementId ?? ''"
        :unlink="true"
        @loaded="handleSyncElementUsedTemplatesLoaded"
      >
        <template #description>
          <div
            v-if="!syncElementUsedTemplatesLoaded"
            class="flex gap-1 align-items-center"
          >
            <ProgressSpinner
              style="width: 25px; height: 25px"
              stroke-width="8"
              fill="var(--surface-ground)"
              animation-duration="1.0s"
              aria-label="Custom ProgressSpinner"
            />
          </div>
          <p
            v-else
          >
            <b>{{ getSyncElementUsedTemplatesNumberTitle(true) }}</b>
          </p>
          <p>{{ syncElementUsedTemplateListLabelForUnlink }}</p>
        </template>
      </SyncElementUsedTemplates>

      <!-- Loader overlay -->
      <div v-if="unlinkSyncElementLoading">
        <Loader />
      </div>
    </div>
    <template #footer>
      <Button
        :label="t('cancel')"
        class="p-button p-button-secondary"
        :disabled="unlinkSyncElementLoading"
        @click="displayUnlinkSyncElementModal = false"
      />
      <Button
        class="p-button p-button-primary"
        :label="t('save')"
        :disabled="unlinkSyncElementLoading"
        @click="commandUnlinkAndSave"
      />
    </template>
  </Dialog>
  <Dialog
    v-if="exitModeModalVisible"
    :visible="exitModeModalVisible"
    :header="t('templateBuilder.exitModeModal.header')"
    :style="{ width: '60rem' }"
    modal
    @update:visible="hideExitEditModeModal"
  >
    <div class="text-left">
      <span class="text-left">{{ t('templateBuilder.exitModeModal.message') }}</span>
      <HelpMessage
        :message="t('templateBuilder.exitModeModal.helpLink')"
        :link="{
          type: 'gleap',
          articleId: '164',
          label: t('showHelpArticle')
        }"
      />
      <Divider class="exit-edit-mode-modal-divider" />
      <div class="flex gap-2 mt-4 justify-content-end">
        <Button
          :label="t('templateBuilder.leaveWithoutSaving')"
          class="p-button p-button-secondary"
          @click="commandCancelModifications"
        />
        <Button
          :label="t('templateBuilder.editSyncElementActions.unlinkAndSave')"
          class="p-button p-button-secondary"
          @click="showUnlinkAndSaveModal"
        />
        <Button
          :label="t('templateBuilder.editSyncElementActions.saveForAllInstances')"
          class="p-button p-button-primary"
          @click="commandSaveForAllInstances"
        />
      </div>
    </div>
  </Dialog>
  <Dialog
    v-if="isExitCurrentChangesModalVisible"
    :visible="isExitCurrentChangesModalVisible"
    :header="t('templateBuilder.exitCurrentChangesModal.header')"
    :style="{ width: '60rem' }"
    modal
    @update:visible="hideExitCurrentChangeModal"
  >
    <div class="text-left">
      <span class="text-left">{{ t('templateBuilder.exitCurrentChangesModal.message') }}</span>
      <Divider class="exit-edit-mode-modal-divider mt-4" />
      <div class="flex gap-2 mt-4 justify-content-end">
        <Button
          :label="t('templateBuilder.leaveWithoutSaving')"
          class="p-button p-button-secondary"
          @click="leaveWithoutSavingCommand"
        />
        <Button
          :label="t('templateBuilder.saveAndLeave')"
          class="p-button p-button-primary"
          @click="saveAndLeaveCommand"
        />
      </div>
    </div>
  </Dialog>
</template>

<script lang="ts">
import {
  computed,
  ComputedRef,
  defineComponent,
  nextTick,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  ref,
  Ref,
  watch,
} from 'vue';

import { useStore } from '@/store';
import {
  ElementPartTypeEnum,
  HistoryType,
  TemplateParentTypeEnum,
  TemplateStructureEnum,
  TemplateWidth,
  WidgetTypeEnum,
} from '@/types/enums';
import {
  TEMPLATE_PAGE_IDENTIFIER,
  MEDIA_URL_PART_THUMB_GENERATION,
  HIDE_MOBILE_ATTRIBUTE,
  TEMPLATE_SYNC_ELEMENT_IDENTIFIER,
  TEMPLATE_SYNC_ELEMENT_NAME,
  TEMPLATE_SYNC_ELEMENT_CLASS,
  TEMPLATE_PART_ELEMENT_PART_TYPE,
  TEMPLATE_SYNC_ELEMENT_IDs,
  TEMPLATE_PART_ELEMENT_SUBTYPE,
  TEMPLATE_PART_ELEMENT_IDENTIFIER,
} from '@/components/template-builder/utils/constants';
import ConfigurationPanel from '@/components/template-builder/panels/ConfigurationPanel.vue';
import InformationPanel from '@/components/template-builder/panels/InformationPanel.vue';
import TranslationPanel from '@/components/template-builder/panels/TranslationPanel.vue';
import HistoryPanel from '@/components/template-builder/panels/HistoryPanel.vue';
import HistoryRestoreLoader from '@/components/template-builder/HistoryRestoreLoader.vue';
import CssPanel from '@/components/template-builder/panels/CssPanel.vue';
import CssPanelMediaQueries from '@/components/template-builder/panels/CssPanelMediaQueries.vue';
import LoadElementsPanel from '@/components/template-builder/panels/LoadElementsPanel.vue';
import TemplateLiveView from '@/components/template-builder/TemplateLiveView.vue';
import InputTextModal from '@/components/modals/InputTextModal.vue';
import SaveElementModal from '@/components/modals/SaveElementModal.vue';
import WidgetsPanel from '@/components/template-builder/panels/WidgetsPanel.vue';
import ColumnWidgetModal from '@/components/modals/ColumnWidgetModal.vue';
import LoadElementsModal from '@/components/modals/LoadElementsModal.vue';
import SearchElementsModal from '@/components/modals/SearchElementsModal.vue';
import SyncElementUsedTemplates from '@/components/template-builder/SyncElementUsedTemplates.vue';
import useToggleBodyScrollBars from '@/composables/useToggleBodyScrollBars';
import { openHelp } from '@/helpers/Gleap';
import {
  activate,
  getTemplatePartsById,
  clearVersionConflicts,
  deactivate,
  hideConfigurationPanel,
  hideTemplateEditor,
  refreshSectionsConfig,
  refreshTranslations,
  reloadTemplateFromDatabase,
  rename,
  savePartInDatabase,
  saveTemplatesInLocalStorage,
  setPreventDateModificationUpdate,
  setRefreshTemplatesList,
  showConfigurationPanel,
  TemplateEditorState as state,
  toggleAdvancedMode,
  togglePanel,
  updateSectionsInState,
  sendTestNotification,
  getTestPushNotificationData,
  beforeSaveTemplate,
  saveTemplate,
  setTemplateConfigurationKeyInState,
  informationPanelSettingsValidate,
  updateLastRollbackGroupId,
  setTestEmailSent,
  resetNeedToTranslate,
  resetTemplateToDesktop,
  setTemplateWidth,
  refreshSmartProductsListsOriginalStructure,
  refreshAttributeInDOM,
  restoreSmartListsOriginalCode,
  sortTranslationInState,
  removeUnusedTranslations,
  hideLeftToolbar,
  restoreState,
  unlinkSyncElement,
  linkSection,
  addNewSyncElementToSection,
  renamePartInDatabase,
  updateUsedSyncElements,
  getAllSyncElements,
  removeCustomWidgetCss,
  getTemplatePartsBySyncElementIds,
  updateUsedSyncElementsName,
  insertSavedElement,
  startHistoryRestore,
  updateHistoryRestoreProgress,
  endHistoryRestore,
} from '@/composables/template-editor/TemplateEditor';

import EditorModal from '@/components/modals/EditorModal.vue';
import {
  ActiveLiveEditorItemData,
  PrimvueMenuModel,
  SelectedSyncElement,
  SyncElementWarning,
  TemplatePart,
} from '@/types';
import { useI18n } from 'vue-i18n';
import {
  getTemplateIframeDocument,
  isDisplayTemplate,
  isEmailTemplate,
  isFacebookTemplate,
  isPushTemplate,
  isSmsTemplate,
  clearPlaceholder,
  showEditSyncElementWarning,
  getSectionParent,
  getWidgetTypeFromTemplate,
  getElementFromIframe,
  getWidgetType,
  getWidgetLabelTranslationKey,
  generateUniqStructureId,
} from '@/components/template-builder/utils/helpers';
import {
  showToastError,
  showToastSuccess,
  generateSha1Hash,
  asInt,
} from '@/helpers';
import { getShopDefaultLang, UserState } from '@/composables/User';
import {
  createHistory,
  getClosestPointOfSave, getHistoryIndexFromGroupId,
  getLastPointOfSaveId,
  getTemplateHistoryGroupList,
  getTemplateHistoryGroupListPaginated,
  loadHistoryGroups,
  restoreToHistoryPoint,
  HISTORY_PAGE_SIZE,
} from '@/composables/template-editor/History';
import { TemplatesElementsHistoryGroup, TemplatesParts } from '@/types/generated-types/graphql';
import { useConfirm } from 'primevue/useconfirm';
import ConfirmDialog from 'primevue/confirmdialog';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import ProgressSpinner from 'primevue/progressspinner';
import Divider from 'primevue/divider';
import {
  EditingTemplateStatusEnum,
  getEditingTemplateFromCampaign,
  setEditingTemplateFromCampaign,
  setCampaignTemplateInformationFromBuilder,
} from '@/composables/AutomatedScenarios';
import TemplateSelector from '@/components/automated-scenarios/fields/TemplateSelector.vue';
import { duplicateTemplate } from '@/composables/shop/Templates';
import SendTestModal, { Suggestion } from '@/components/modals/SendTestModal.vue';
import {
  destroyAllSortables,
  resetListeners,
  trackMousePosition,
  stopScrolling,
} from '@/components/template-builder/utils/listeners';
import { refreshSmartProductList } from '@/components/template-builder/callbacks';
import { removeActiveItemElementDecorations } from '@/components/template-builder/utils/active-item-builder';
import DuplicateModal from '@/components/modals/DuplicateModal.vue';
import { nestGet, securedNestPost } from '@/composables/nestApi';
import getStructurePanelConfigMap from '@/components/template-builder/config/structure-panel-config';
import ChooseZipFileModal from '@/components/modals/ChooseZipFileModal.vue';
import Loader from '@/components/layout/Loader.vue';
import { delay } from '@/helpers/helpers';
import HelpMessage from '@/components/common/HelpMessage.vue';
import SyncElementNotUsedTemplates from '@/components/template-builder/SyncElementNotUsedTemplates.vue';
import { updateIframeTranslations, fixMissingTranslations } from './utils/translate';
import { duplicateElement } from './utils/action-buttons-handlers';
import { cleanupDynamicStyle } from './utils/parser';
import { updateSyncElementsInOpenedTemplates } from './utils/sync-elements-helpers';
import { isPanelVisible } from './config/sync-elements-config';

export default defineComponent({
  name: 'TemplateEditor',

  components: {
    HelpMessage,
    DuplicateModal,
    TemplateSelector,
    SendTestModal,
    EditorModal,
    ConfigurationPanel,
    InformationPanel,
    TranslationPanel,
    HistoryPanel,
    HistoryRestoreLoader,
    CssPanel,
    CssPanelMediaQueries,
    LoadElementsPanel,
    TemplateLiveView,
    InputTextModal,
    SaveElementModal,
    WidgetsPanel,
    ConfirmDialog,
    ColumnWidgetModal,
    LoadElementsModal,
    SearchElementsModal,
    ChooseZipFileModal,
    Dialog,
    SyncElementUsedTemplates,
    Button,
    ProgressSpinner,
    Divider,
    Loader,
    SyncElementNotUsedTemplates,
  },

  setup() {
    const { t, locale } = useI18n();
    const idShop: number = UserState.activeShop ? UserState.activeShop.id : 0;

    const store = useStore();
    const confirm = useConfirm();
    
    // Variables pour stocker les données du conflit de version
    const currentConflictMessage = ref<string>('');
    const currentConflictLocalDate = ref<string>('');
    const currentConflictRemoteDate = ref<string>('');
    
    const selectedTemplateId = computed(() => state.template?.id ?? 0);
    const selectedTemplateType = computed(() => state.template?.type ?? '');
    const selectedSyncElement = computed<SelectedSyncElement>(() => store.getters['liveEditor/getSelectedSyncElement']);
    const selectedTemplateName = computed(() => {
      if (selectedSyncElement.value?.elementName) {
        return selectedSyncElement.value.elementName;
      }
      return state.template?.informations.name;
    });
    const selectedTemplateIdCampaign = computed(() => state.template?.idCampaign ?? null);
    const selectedStructure = computed(() => store.getters['liveEditor/getSelectedStructure']);
    const syncElementWarning = computed<SyncElementWarning>(() => store.getters['liveEditor/getSyncElementWarning']);
    const exitModeModalVisible = computed<boolean>(() => store.getters['liveEditor/getExitEditModeModalVisible']);
    const exitModeModalCallback = computed<Function | null>(() => store.getters['liveEditor/getExitEditModeCallback']);
    const displayElementsModal: ComputedRef<boolean> = computed(() => state.isLoadElementsModalVisible);
    const subtitle = computed(() => {
      if (selectedSyncElement.value) {
        const modelName = state.template?.informations.name || '';

        if (selectedSyncElement.value.elementPartType) {
          const elementPartTypeTranslation = t(`templateBuilder.toolbar.header.subtitle.elementPartTypes.${selectedSyncElement.value.elementType}`);
          return t(
            'templateBuilder.toolbar.header.subtitle.editElementPartWithModel',
            [elementPartTypeTranslation, modelName],
          );
        }
        const elementTypeTranslation = t(`templateBuilder.toolbar.header.subtitle.elementTypes.${selectedSyncElement.value.elementType}`);
        return t(
          'templateBuilder.toolbar.header.subtitle.editElementWithModel',
          [elementTypeTranslation, modelName],
        );
      }
      return '';
    });

    const structurePanelConfigMap = getStructurePanelConfigMap();
    const selectedStructureActions = computed(() => {
      if (isDisplayTemplate(selectedTemplateType.value) || isEmailTemplate(selectedTemplateType.value)) {
        const structureType = selectedStructure.value?.type;
        if (!structureType) return [];
        const structureTypeConfig = structurePanelConfigMap[structureType];
        if (!structureTypeConfig) return [];
        return structureTypeConfig.actions;
      }
      return [];
    });

    const isConfigurationPanelVisible = computed(() => state.isConfigurationPanelVisible);
    const isConfigurationPanelClosable = computed(() => {
      if (selectedTemplateType.value !== TemplateParentTypeEnum.SMS
      && selectedTemplateType.value !== TemplateParentTypeEnum.PUSHNOTIFICATIONS) {
        if (selectedSyncElement.value && [TemplateStructureEnum.WIDGET, TemplateStructureEnum.SECTION].includes(selectedSyncElement.value.elementType as TemplateStructureEnum)) {
          return false;
        }
        return true;
      }
      return false;
    });
    const isLeftToolbarVisible = computed(() => {
      let returnValue = false;
      if (state.isLeftToolbarVisible && (state.leftToolbar.show.configurationPanel || state.leftToolbar.show.designPanel) && selectedStructure.value) {
        returnValue = true;
      } else if (state.isLeftToolbarVisible && state.leftToolbar.show.configurationPanel && !selectedStructure.value) {
        returnValue = true;
      } else if (state.isLeftToolbarVisible && !state.leftToolbar.show.configurationPanel && !state.leftToolbar.show.designPanel) {
        returnValue = true;
      }

      return returnValue;
    });
    const isWidgetPanelClosable = computed(() => {
      if (selectedTemplateType.value === TemplateParentTypeEnum.EMAIL && !selectedSyncElement.value) {
        return false;
      }
      if (selectedTemplateType.value === TemplateParentTypeEnum.EMAIL && selectedSyncElement.value && selectedSyncElement.value.elementType !== TemplateStructureEnum.SECTION) {
        return true;
      }
      return true;
    });
    const isTranslationPanelClosable = computed(() => !state.template?.informations.imported);
    const advancedModeEnabled = computed(() => {
      if (state.advancedModeEnabled) {
        return true;
      }
      if (selectedSyncElement.value && isPanelVisible(selectedSyncElement.value.elementType, 'advancedCssPanel', selectedSyncElement.value.elementPartType)) {
        return true;
      }
      return false;
    });
    const showTemplateSelector = ref(false);
    const showDuplicationModal = ref(false);
    const displaySyncElementTemplatesModal = ref(false);
    const displaySaveSyncElementTemplatesModal = ref(false);
    const displayUnlinkSyncElementModal = ref(false);

    const isChooseZipFileModalVisible = computed(() => store.getters['liveEditor/getIsChooseZipFileModalVisible']);
    const isSendTestModalVisible = computed(() => store.state.general.sendTestModalVisible);

    const width = computed({
      get() {
        return state.templateWidth;
      },
      set(newWidth: string) {
        setTemplateWidth(newWidth);
      },
    });

    const historyList: Ref<TemplatesElementsHistoryGroup[]> = ref([]);
    const selectedHistory: Ref<TemplatesElementsHistoryGroup|null> = ref(null);
    const historyHasMore = ref(false);
    const historyIsLoadingMore = ref(false);
    const historyTotalItems = ref(0);
    const lastHistoryGroupId: ComputedRef<number|null> = computed(() => state.template?.configuration.lastIdTemplateElementsHistoryGroup);
    const currentHistoryGroupId: ComputedRef<number|null> = computed(() => state.template?.configuration.currentIdTemplateElementsHistoryGroup ?? lastHistoryGroupId.value);
    const firstHistoryGroupId: ComputedRef<number|null> = computed(() => {
      let lastHistoryPoint: TemplatesElementsHistoryGroup|null = null;

      if (historyList.value.length > 0) {
        lastHistoryPoint = historyList.value[historyList.value.length - 1];
      }

      return lastHistoryPoint?.id_template_elements_history_group ?? null;
    });

    // key used to rerender live preview on history change
    const livePreviewKey = ref(0);
    // flag to preserve current panel when restoring history
    const isRestoringHistory = ref(false);

    const displayRenameTemplateModal: Ref<boolean> = ref(false);
    const valueRenameTemplateModal: Ref<string> = ref('');

    const displaySaveElementModal: ComputedRef<boolean> = computed(() => store.state.liveEditor.saveElementModalPayload.visible);
    const typeSaveElementModal: ComputedRef<TemplateStructureEnum | null> = computed(() => store.state.liveEditor.saveElementModalPayload.type);

    const nameSaveElementModal: Ref<string> = ref('');
    const syncStateSaveElementModal: Ref<boolean> = ref(false);
    const saveElementModalRef: Ref<any> = ref(null);

    // Search Elements Modal variables
    const displaySearchElementsModal: Ref<boolean> = ref(false);
    const searchElementsType: Ref<string> = ref('');
    const searchElementsPartType: Ref<string> = ref('');
    const searchElementsSubtype: Ref<string> = ref('');
    const includeAllElementsPartType: Ref<boolean> = ref(false);

    const informationPanelValidation = ref<any>({});
    const newListName = ref();

    const syncElementUsedTemplatesTotal = ref(0);
    const syncElementUsedTemplatesLoaded = ref(false);
    const syncElementNotUsedTemplatesTotal = ref(0);
    const selectedNotUsedTemplates = ref<number[]>([]);
    const selectedNotUsedTemplatesSections = ref<Record<number, string[]>>({});
    const syncElementNotUsedTemplatesRef = ref();

    const syncElementUsedTemplatesRef = ref();

    /* Check if this template was edited from a campaign.d. */
    const openingFromCampaign: ComputedRef<boolean> = computed(() => selectedTemplateIdCampaign.value !== null
      && getEditingTemplateFromCampaign() !== null);

    const setLastHistory = (historyGroupId: number | null) => {
      if (historyGroupId) {
        const currentHistory: TemplatesElementsHistoryGroup[] = historyList.value
          .filter((history: TemplatesElementsHistoryGroup) => history.id_template_elements_history_group === historyGroupId);

        if (currentHistory.length) {
          [selectedHistory.value] = currentHistory;
        }
      }
    };

    const handleChangeHistory = async (index: number) => {
      store.commit('liveEditor/setCheckSyncElements', true); // Set this to make sure sync elements are checked in case of update
      isRestoringHistory.value = true;
      startHistoryRestore(1);
      try {
        await restoreToHistoryPoint(historyList.value, index, (current, total, actionDetail) => {
          updateHistoryRestoreProgress(current, total, actionDetail);
        });
        refreshSectionsConfig(JSON.parse(historyList.value[index].sections_order_after ?? '').map((id: string) => id.toString()));
        refreshTranslations();
        selectedHistory.value = historyList.value[index];
        setTemplateConfigurationKeyInState('currentIdTemplateElementsHistoryGroup', selectedHistory.value.id_template_elements_history_group);
        setLastHistory(selectedHistory.value.id_template_elements_history_group);
        livePreviewKey.value += 1;
      } finally {
        endHistoryRestore();
      }
    };

    const handlePreviousHistory = () => {
      const currentIndex = historyList.value
        .findIndex((current: TemplatesElementsHistoryGroup) => current.id_template_elements_history_group === selectedHistory.value?.id_template_elements_history_group);

      if (currentIndex === undefined) handleChangeHistory(0);
      // eslint-disable-next-line prefer-destructuring
      else if (currentIndex < (historyList.value.length - 1)) handleChangeHistory((currentIndex + 1));
    };

    const handleNextHistory = () => {
      const currentIndex = historyList.value
        .findIndex((current: TemplatesElementsHistoryGroup) => current.id_template_elements_history_group === selectedHistory.value?.id_template_elements_history_group);
      if (currentIndex) handleChangeHistory((currentIndex - 1));
    };

    const handleLoadMoreHistory = async () => {
      if (historyIsLoadingMore.value || !historyHasMore.value) return;

      historyIsLoadingMore.value = true;
      try {
        const result = await getTemplateHistoryGroupListPaginated(historyList.value.length, HISTORY_PAGE_SIZE);
        historyList.value = [...historyList.value, ...result.items];
        historyHasMore.value = result.hasMore;
        historyTotalItems.value = result.total;
      } finally {
        historyIsLoadingMore.value = false;
      }
    };

    useToggleBodyScrollBars();

    const prepareStructureConfiguration = () => {
      store.dispatch('liveEditor/prepareStructureConfiguration', {
        type: TemplateStructureEnum.PAGE,
        identifier: TEMPLATE_PAGE_IDENTIFIER,
      });
    };

    const getPageConfig = () => {
      if (isConfigurationPanelVisible.value && selectedStructure.value?.type === TemplateStructureEnum.PAGE) {
        hideConfigurationPanel();
        return;
      }
      prepareStructureConfiguration();
      showConfigurationPanel();
    };

    const handleSaveElementClose = () => {
      store.commit('liveEditor/hideSaveElementModal');
      // Reset the element name when modal closes
      nameSaveElementModal.value = '';
    };

    // Search Elements Modal handlers
    const handleSearchElements = (searchParams: { elementType: string; elementPartType: string; elementSubtype: string; includeAllElementsPartType: boolean }) => {
      searchElementsType.value = searchParams.elementType;
      searchElementsPartType.value = searchParams.elementPartType;
      searchElementsSubtype.value = searchParams.elementSubtype;
      includeAllElementsPartType.value = searchParams.includeAllElementsPartType;
      displaySearchElementsModal.value = true;
    };

    const handleSearchElementsClose = () => {
      displaySearchElementsModal.value = false;
      searchElementsType.value = '';
      searchElementsPartType.value = '';
      searchElementsSubtype.value = '';
      includeAllElementsPartType.value = false;
    };

    const handleSearchElementSelected = (elementData?: { elementName: string; elementId: number }) => {
      if (elementData) {
        // Set the selected element name in the save modal
        nameSaveElementModal.value = elementData.elementName;
        // Mark as selected from search in the next tick to ensure the modal is rendered
        nextTick(() => {
          if (saveElementModalRef.value && saveElementModalRef.value.markAsSelectedFromSearch) {
            saveElementModalRef.value.markAsSelectedFromSearch(elementData.elementId);
          }
        });
      }
      // Close the search modal
      handleSearchElementsClose();
    };

    const toEraseElementId = ref();
    const toEraseElementSyncId = ref('');
    const toEraseIsNewSync = ref(false);

    const updateOtherSyncElementsInTempalte = async (elementToDuplicate: HTMLElement | null, data: {
      syncElementId: string;
      elementId: string;
      elementType: string;
      elementPartType?: string;
      elementSubtype?: string;
      result?: any;
    }) => {
      const otherSyncElementsToUpdate = getAllSyncElements(data.syncElementId ?? '', data.elementId);
      const itemData: ActiveLiveEditorItemData = {
        selector: `#${data.elementId}`,
        type: data.elementType as TemplateStructureEnum,
      };

      // eslint-disable-next-line no-restricted-syntax
      for (const otherSyncElementToUpdate of Array.from(otherSyncElementsToUpdate)) {
        if (elementToDuplicate) {
          // Duplicate if the template part is a widget or section itself
          if (!data.elementPartType) {
            // eslint-disable-next-line no-await-in-loop
            await duplicateElement(
              itemData,
              itemData.type,
              elementToDuplicate,
              null,
              null,
              null,
              null,
              null,
              false,
              `#${otherSyncElementToUpdate.id}`,
              otherSyncElementToUpdate.getAttribute('data-spmelementid') || '',
            );
          } else if (data.elementPartType === ElementPartTypeEnum.STYLES && data.result) {
            if (getWidgetTypeFromTemplate(data.elementSubtype || '') === WidgetTypeEnum.SMART_PRODUCT_LIST) {
              // eslint-disable-next-line no-await-in-loop
              await duplicateElement(
                itemData,
                itemData.type,
                elementToDuplicate,
                null,
                null,
                null,
                null,
                null,
                false,
                `#${otherSyncElementToUpdate.id}`,
                otherSyncElementToUpdate.getAttribute('data-spmelementid') || '',
                true,
              );
            } else {
              // eslint-disable-next-line no-await-in-loop
              await insertSavedElement(data.result.newPart, `#${otherSyncElementToUpdate.id}`, 'top', false, otherSyncElementToUpdate.getAttribute('data-spmelementid') || '');
            }
          }
        }
      }
      // Remove sync elements if the part is not a whole element
      if (!data.elementPartType
        || (data.elementPartType === ElementPartTypeEnum.STYLES
        && getWidgetTypeFromTemplate(data.elementSubtype || '') === WidgetTypeEnum.SMART_PRODUCT_LIST)
      ) {
        // eslint-disable-next-line no-restricted-syntax
        for (const otherSyncElementToUpdate of Array.from(otherSyncElementsToUpdate)) {
          otherSyncElementToUpdate.remove();
          removeCustomWidgetCss(`#${otherSyncElementToUpdate.id}`);
        }
      }
    };

    const updateSyncElementsInTemplate = async (data: {
      elementType: string;
      elementName: string;
      elementId: string;
      syncElementId: string;
      elementPartType?: string;
      elementSubtype?: string;
      result?: any;
    }) => {
      if (data.elementType !== TemplateStructureEnum.DESIGN) {
        const template = getTemplateIframeDocument();
        const elementToDuplicate: HTMLElement | null = template.querySelector(`#${data.elementId}`);
        if (elementToDuplicate) {
          elementToDuplicate.setAttribute(TEMPLATE_SYNC_ELEMENT_NAME, data.elementName || '');
        }
        await updateOtherSyncElementsInTempalte(elementToDuplicate, data);
      } else {
        const template = getTemplateIframeDocument();
        if (template) {
          template.body.setAttribute(TEMPLATE_SYNC_ELEMENT_NAME, data.elementName ?? '');
        }
      }

      await nextTick();

      if (data.result) {
        if (data.result.newPart.type === TemplateStructureEnum.SECTION) {
          const newElementData = JSON.parse(data.result.newPart.data);
          if (newElementData[TEMPLATE_SYNC_ELEMENT_IDENTIFIER]
            && !newElementData[TEMPLATE_PART_ELEMENT_PART_TYPE]
            && newElementData[TEMPLATE_SYNC_ELEMENT_IDs]
            && Array.isArray(newElementData[TEMPLATE_SYNC_ELEMENT_IDs])
            && newElementData[TEMPLATE_SYNC_ELEMENT_IDs].length > 0
          ) {
            const syncWidgetIds = newElementData[TEMPLATE_SYNC_ELEMENT_IDs];
            // eslint-disable-next-line no-restricted-syntax
            for (const syncWidgetId of syncWidgetIds) {
              // eslint-disable-next-line no-await-in-loop
              const syncWidgetPart = await getTemplatePartsBySyncElementIds(idShop, [syncWidgetId]);
              if (syncWidgetPart.items && syncWidgetPart.items.length > 0) {
                const syncWidgetPartItem = syncWidgetPart.items[0];
                updateSyncElementsInOpenedTemplates(state?.template?.id ?? 0, syncWidgetPartItem);
                const template = getTemplateIframeDocument();
                if (template) {
                  let otherWidgets = Array.from(template.querySelectorAll(`[${TEMPLATE_SYNC_ELEMENT_IDENTIFIER}="${syncWidgetId}"]`));
                  if (otherWidgets.length > 0) {
                    otherWidgets = otherWidgets.filter((widget) => {
                      const parentSection = widget.closest('[data-spmelementid]');
                      if (parentSection && parentSection.hasAttribute(TEMPLATE_SYNC_ELEMENT_IDENTIFIER) && !parentSection.hasAttribute(TEMPLATE_PART_ELEMENT_PART_TYPE)) {
                        return true;
                      }
                      return false;
                    });
                    // eslint-disable-next-line no-restricted-syntax
                    for (const otherWidget of otherWidgets) {
                      const syncWidgetData = JSON.parse(syncWidgetPartItem.data || '{}');
                      // eslint-disable-next-line no-await-in-loop
                      await updateOtherSyncElementsInTempalte(otherWidget as HTMLElement, {
                        elementType: TemplateStructureEnum.WIDGET,
                        elementId: otherWidget.id,
                        syncElementId: syncWidgetId,
                        elementPartType: syncWidgetData[TEMPLATE_PART_ELEMENT_PART_TYPE] || '',
                        elementSubtype: syncWidgetData[TEMPLATE_PART_ELEMENT_SUBTYPE] || '',
                        result: {
                          newPart: syncWidgetPartItem,
                        },
                      });
                    }
                  }
                }
              }
            }
          }
        }
        updateSyncElementsInOpenedTemplates(state?.template?.id ?? 0, data.result.newPart);
      }

      cleanupDynamicStyle();
      removeActiveItemElementDecorations();
      updateSectionsInState(false);
      removeUnusedTranslations();
      refreshSectionsConfig();
      resetListeners();
      refreshSmartProductList();
    };

    const updateParentSection = async (syncParentSyncId: string, syncParentId: string) => {
      if (syncParentSyncId && syncParentId) {
        const parentTemplatePart = await getTemplatePartsBySyncElementIds(idShop, [syncParentSyncId]);
        if (parentTemplatePart.items && parentTemplatePart.items.length > 0) {
          const parentTemplatePartItem = parentTemplatePart.items[0];
          const result = await savePartInDatabase(
            idShop,
            parentTemplatePartItem.type || '',
            parentTemplatePartItem.name || '',
            `#${syncParentId}`,
            asInt(parentTemplatePartItem.id_template_parts),
            true,
            syncParentSyncId,
          );

          if (result && result.newPart) {
            await securedNestPost(
              'v4',
              '/template/updateSyncElements',
              {},
              {
                idShop,
                syncElementId: syncParentSyncId,
                elementType: TemplateStructureEnum.SECTION,
                elementId: syncParentId,
                idTemplate: state.template?.id,
                deleted: false,
              },
            );

            await updateSyncElementsInTemplate({
              elementType: TemplateStructureEnum.SECTION,
              elementName: parentTemplatePartItem.name || '',
              elementId: syncParentId,
              syncElementId: syncParentSyncId,
              result,
            });
          }
        }
      }
    };

    const saveElement = async (
      element: {
        name: string;
        type: string;
        isSync: boolean;
        renameElement?: {
          itemId: number;
          itemType: string;
          oldName: string;
          syncElementId?: string;
        };
        elementPartType?: ElementPartTypeEnum;
        fromSyncElement?: boolean;
        syncParentSyncId?: string;
        syncParentId?: string;
        selectedElementId?: number;
      },
      done: Function,
    ) => {
      if (!element.renameElement) {
        const activeItemSelector = computed(() => store.getters['liveEditor/getSaveElementSelector']);

        // Check if we have a selected element ID for overwrite (from search selection)
        let existingTemplatePart: any = null;
        if (element.selectedElementId) {
          existingTemplatePart = await getTemplatePartsById(element.selectedElementId);
        }

        const updateElementInState = (syncId: string) => {
          const applyClassAndAttribute = (isDesign = false) => {
            const template = getTemplateIframeDocument();
            if (template) {
              let elementHtml = isDesign ? template.body : template.body.querySelector(activeItemSelector.value);
              if (isDisplayTemplate(state.template?.type) && isDesign) {
                elementHtml = template.querySelector('#spm_body');
              }
              if (elementHtml) {
                if (!element.fromSyncElement) {
                  elementHtml.setAttribute(TEMPLATE_SYNC_ELEMENT_IDENTIFIER, syncId);
                  elementHtml.setAttribute(TEMPLATE_SYNC_ELEMENT_NAME, element.name);
                  if (element.type === TemplateStructureEnum.SECTION && !element.elementPartType) {
                    const styleSyncChilds = elementHtml.querySelectorAll(`[${TEMPLATE_SYNC_ELEMENT_IDENTIFIER}][${TEMPLATE_PART_ELEMENT_PART_TYPE}="styles"]`);
                    styleSyncChilds.forEach((styleSyncChild: HTMLElement) => {
                      if (!styleSyncChild.hasAttribute(TEMPLATE_PART_ELEMENT_IDENTIFIER)) {
                        styleSyncChild.setAttribute(TEMPLATE_PART_ELEMENT_IDENTIFIER, generateUniqStructureId());
                      }
                    });
                  }
                }
                if (!isDesign && !element.elementPartType) {
                  elementHtml.classList.add(TEMPLATE_SYNC_ELEMENT_CLASS);
                  elementHtml.removeAttribute(TEMPLATE_PART_ELEMENT_PART_TYPE);
                }
                if (element.elementPartType && !element.fromSyncElement) {
                  elementHtml.setAttribute(TEMPLATE_PART_ELEMENT_PART_TYPE, element.elementPartType);
                  if (isEmailTemplate(state.template?.type)) {
                    const parentSection = getSectionParent(elementHtml);
                    if (parentSection && parentSection.hasAttribute(TEMPLATE_SYNC_ELEMENT_IDENTIFIER) && !parentSection.hasAttribute(TEMPLATE_PART_ELEMENT_PART_TYPE)) {
                      elementHtml.setAttribute(TEMPLATE_PART_ELEMENT_IDENTIFIER, generateUniqStructureId());
                    }
                  }
                }
              }
              return elementHtml;
            }
            return null;
          };

          let elementHtml: HTMLElement | null = null;

          switch (element.type) {
            case TemplateStructureEnum.SECTION:
              elementHtml = applyClassAndAttribute();
              if (elementHtml) {
                linkSection(elementHtml.getAttribute('data-spmelementid'), syncId, element.name);
              }
              break;
            case TemplateStructureEnum.DESIGN:
              elementHtml = applyClassAndAttribute(true);
              linkSection('', syncId, element.name, TemplateStructureEnum.DESIGN);
              break;
            default:
              elementHtml = applyClassAndAttribute();
              if (elementHtml) {
                const parentSection = getSectionParent(elementHtml as HTMLElement);
                if (parentSection && !element.fromSyncElement) {
                  addNewSyncElementToSection(parentSection.getAttribute('data-spmelementid'), syncId);
                }
              }
          }

          if (elementHtml) {
            return elementHtml.id;
          }
          return '';
        };

        const reset = (newSection: TemplatePart) => {
          store.commit('liveEditor/hideSaveElementModal');
          store.dispatch('templateEditor/updateSection', { section: newSection });
        };

        const save = async (existingPart: any) => {
          let existingSyncId = '';
          if (existingPart && element.isSync) {
            const existingPartData = JSON.parse(existingPart.data || '{}');
            if (existingPartData[TEMPLATE_SYNC_ELEMENT_IDENTIFIER]) {
              existingSyncId = existingPartData[TEMPLATE_SYNC_ELEMENT_IDENTIFIER];
            }
          }
          const existingPartId = existingPart && existingPart.id_template_parts ? existingPart.id_template_parts : null;
          const newPart = await savePartInDatabase(
            idShop,
            element.type,
            element.name,
            activeItemSelector.value,
            existingPartId,
            element.isSync,
            existingSyncId,
            element.elementPartType,
          );
          let elementId = '';
          if (newPart) {
            const { newSection, syncId } = newPart;
            if (element.isSync) {
              elementId = updateElementInState(syncId);
              updateSectionsInState(false);
              if (!element.elementPartType) {
                await hideLeftToolbar();
              } else if (element.elementPartType === ElementPartTypeEnum.STYLES && selectedStructure.value) {
                const structureConfigurationData: Record<string, any> = {
                  type: selectedStructure.value.type,
                  identifier: selectedStructure.value.identifier,
                };
                if (selectedStructure.value.type === TemplateStructureEnum.WIDGET) {
                  const widgetType = getWidgetTypeFromTemplate(selectedStructure.value.identifier);
                  if (widgetType) {
                    structureConfigurationData.widgetType = widgetType;
                  }
                }
                store.commit('liveEditor/resetSelectedStructure');
                await nextTick();
                store.dispatch('liveEditor/prepareStructureConfiguration', structureConfigurationData);
              }
            }
            if (element.syncParentSyncId && element.syncParentId) {
              await updateParentSection(element.syncParentSyncId, element.syncParentId);
            }
            if (newSection) {
              reset(newSection);
            }
          }
          return { newPart, elementId };
        };

        if (typeof existingTemplatePart === 'string' && existingTemplatePart !== '') {
          // Show error
          await showToastError(t('errorMessages.GENERIC_ERROR'));
        } else if (existingTemplatePart) {
          toEraseElementId.value = existingTemplatePart.id_template_parts;
          const existingPartData = JSON.parse(existingTemplatePart.data || '{}');
          if (existingPartData[TEMPLATE_SYNC_ELEMENT_IDENTIFIER]) {
            toEraseElementSyncId.value = existingPartData[TEMPLATE_SYNC_ELEMENT_IDENTIFIER];
          }
          if (element.isSync) {
            toEraseIsNewSync.value = true;
          }

          // Generate dynamic element label based on type and elementPartType
          const getElementLabel = () => {
            const partData = JSON.parse(existingTemplatePart.data || '{}');
            const partElementPartType = partData[TEMPLATE_PART_ELEMENT_PART_TYPE];
            const partElementSubtype = partData[TEMPLATE_PART_ELEMENT_SUBTYPE];

            if (partElementPartType) {
              const subtypeLabel = partElementSubtype ? t(getWidgetLabelTranslationKey(getWidgetType(partElementSubtype) as WidgetTypeEnum)) : '';
              return t(
                `templateBuilder.panels.loadElements.chip.elementPart.${partElementPartType}`,
                [
                  t(
                    `templateBuilder.panels.loadElements.chip.elementPart.elements.${existingTemplatePart.type}`,
                    [subtypeLabel],
                  ),
                ],
              );
            }
            return t(`templateBuilder.panels.loadElements.chip.${existingTemplatePart.type}`);
          };

          // If used, we ask confirmation to erase the old one
          confirm.require({
            group: 'saveElement',
            message: t('templateBuilder.confirm.saveNewSection.duplicateName.message', [getElementLabel(), element.name]),
            header: t('templateBuilder.confirm.saveNewSection.duplicateName.header'),
            icon: 'far fa-exclamation-triangle',
            acceptLabel: t('yes'),
            acceptClass: 'p-button-danger',
            rejectLabel: t('no'),
            rejectClass: 'p-button-secondary',
            accept: async () => {
              const result = await save(existingTemplatePart);
              if (result) {
                if (element.isSync) {
                  let newPartData;
                  let elementSubtype = null;
                  if (result.newPart) {
                    newPartData = JSON.parse(result.newPart.data || '{}');
                    if (newPartData[TEMPLATE_PART_ELEMENT_SUBTYPE]) {
                      elementSubtype = newPartData[TEMPLATE_PART_ELEMENT_SUBTYPE];
                    }
                  }
                  await securedNestPost(
                    'v4',
                    '/template/updateSyncElements',
                    {},
                    {
                      idShop,
                      syncElementId: toEraseElementSyncId.value,
                      elementType: element.type,
                      elementId: '',
                      idTemplate: state.template?.id,
                      deleted: false,
                      initRedirect: 'all',
                    },
                  );

                  await updateSyncElementsInTemplate({
                    elementType: element.type,
                    elementName: element.name,
                    elementId: result.elementId,
                    syncElementId: toEraseElementSyncId.value,
                    elementPartType: element.elementPartType,
                    elementSubtype,
                    result,
                  });
                } else {
                  await securedNestPost(
                    'v4',
                    '/template/updateSyncElements',
                    {},
                    {
                      idShop,
                      syncElementId: toEraseElementSyncId.value,
                      elementType: element.type,
                      idTemplate: state.template?.id,
                      deleted: true,
                    },
                  );
                  updateSyncElementsInOpenedTemplates(state.template?.id ?? 0, result.newPart as TemplatesParts, true);
                }
              }
              toEraseElementId.value = null;
              toEraseElementSyncId.value = '';
              toEraseIsNewSync.value = false;
              nameSaveElementModal.value = '';
              done();
            },
            reject: () => {
              store.commit('liveEditor/hideSaveElementModal');
              toEraseElementId.value = null;
              toEraseElementSyncId.value = '';
              toEraseIsNewSync.value = false;
              nameSaveElementModal.value = '';
              done(true, false);
            },
          });
        } else {
          // No part with same name
          await save(null);
          done();
          nameSaveElementModal.value = '';
          syncStateSaveElementModal.value = false;
        }
      } else {
        try {
          if (element.renameElement.oldName.toLowerCase().trim() !== element.name.toLowerCase().trim()) {
            await renamePartInDatabase(element.renameElement.itemId, element.name);

            if (element.renameElement.syncElementId) {
              await securedNestPost(
                'v4',
                '/template/updateSyncElements',
                {},
                {
                  idShop,
                  syncElementId: element.renameElement.syncElementId,
                  elementType: element.renameElement.itemType,
                  idTemplate: state.template?.id,
                  deleted: false,
                },
              );
              updateUsedSyncElementsName(element.renameElement.syncElementId || '', element.name);
              const newElement = await getTemplatePartsById(element.renameElement.itemId);
              if (newElement) {
                if (newElement.type === TemplateStructureEnum.SECTION) {
                  const newElementData = JSON.parse(newElement.data || '{}');
                  if (newElementData[TEMPLATE_SYNC_ELEMENT_IDENTIFIER]
                    && !newElementData[TEMPLATE_PART_ELEMENT_PART_TYPE]
                    && newElementData[TEMPLATE_SYNC_ELEMENT_IDs]
                    && Array.isArray(newElementData[TEMPLATE_SYNC_ELEMENT_IDs])
                    && newElementData[TEMPLATE_SYNC_ELEMENT_IDs].length > 0
                  ) {
                    const syncWidgetIds = newElementData[TEMPLATE_SYNC_ELEMENT_IDs];
                    // eslint-disable-next-line no-restricted-syntax
                    for (const syncWidgetId of syncWidgetIds) {
                      // eslint-disable-next-line no-await-in-loop
                      const syncWidgetPart = await getTemplatePartsBySyncElementIds(idShop, [syncWidgetId]);
                      if (syncWidgetPart.items && syncWidgetPart.items.length > 0) {
                        const syncWidgetPartItem = syncWidgetPart.items[0];
                        updateSyncElementsInOpenedTemplates(state?.template?.id ?? 0, syncWidgetPartItem);
                      }
                    }
                  }
                }
                updateSyncElementsInOpenedTemplates(state?.template?.id ?? 0, newElement);
              }
            }

            store.dispatch('templateEditor/updateSectionName', { id: element.renameElement.itemId, name: element.name });
            done();
            nameSaveElementModal.value = '';
            syncStateSaveElementModal.value = false;
          }
        } catch (error) {
          showToastError(t('errorMessages.GENERIC_ERROR'));
          done(false);
        }
      }
    };

    const sendTestTemplate = async (params: Suggestion[]) => {
      if (state.template) {
        store.commit('general/showTheSpinner');

        // Send query to NestJS
        const getData = {
          type: selectedTemplateType.value,
          params,
        };

        const dataQuery = btoa(encodeURIComponent(JSON.stringify(getData)));
        const result = await nestGet('v4', `/template/test/${state.template.id}/${dataQuery}`, {}, '');

        if (result && result.success) {
          if (isEmailTemplate(selectedTemplateType.value)) {
            await showToastSuccess('emailSent');
          } else if (isSmsTemplate(selectedTemplateType.value)) {
            await showToastSuccess('smsSent');
          }
        } else {
          await showToastError(result && result.message ? t(`errorMessages.${result.message}`) : t('errorMessages.GENERIC_ERROR'));
        }

        store.commit('general/hideTheSpinner');
      }
    };

    const executeBeforeTemplateSave = async () => {
      if (!isSmsTemplate(selectedTemplateType.value) && !isPushTemplate(selectedTemplateType.value) && !isFacebookTemplate(selectedTemplateType.value)) {
        await resetTemplateToDesktop();
      }

      return beforeSaveTemplate(t);
    };

    // Function executer when the user click on the save & exit button or the save & stay button
    const executeTemplateSave = async (exit = false, treatSwitchToCampaign = true, validateInformationPanel = true, historyType = HistoryType.POINT_OF_SAVE) => {
      store.commit('general/showTheSpinner');
      let validation: Record<string, any> = { success: true };

      if (validateInformationPanel) {
        // Validate InformationPanel values
        validation = await informationPanelSettingsValidate(true);
        informationPanelValidation.value = validation.validate;
      }

      if (!validation.success) {
        // Open InformationPanel to see errors
        if (!state.isLeftToolbarVisible || !state.leftToolbar.show.informationPanel) {
          await togglePanel('informationPanel');
        }
      } else if (await executeBeforeTemplateSave()) {
        // Update state
        updateSectionsInState(false);

        await createHistory(historyType);
        if (await saveTemplate()) {
          if (exit) {
            if (openingFromCampaign.value && treatSwitchToCampaign) {
              const editingTemplateFromCampaign = getEditingTemplateFromCampaign();
              if (editingTemplateFromCampaign !== null && editingTemplateFromCampaign.status === EditingTemplateStatusEnum.IN_TEMPLATE_EDITOR) {
                editingTemplateFromCampaign.status = EditingTemplateStatusEnum.SAVE_QUIT_TEMPLATE_EDITOR;
                setEditingTemplateFromCampaign(editingTemplateFromCampaign);
                setCampaignTemplateInformationFromBuilder(state.template?.informations);
              }
            }

            deactivate(state.template?.id ?? 0);
          } else {
            // Refresh smart products lists and listeners
            await refreshSmartProductList();
            resetListeners();
          }
        }
      }

      store.commit('general/hideTheSpinner');
    };

    const restoreTemplateBeforeEdit = () => {
      const currentState = store.getters['liveEditor/getCurrentTemplateState'];
      if (currentState) {
        restoreState(currentState.index, currentState.template);
        store.commit('liveEditor/destroySortableInstances');
        livePreviewKey.value += 1;
        store.commit('liveEditor/setCurrentTemplateState', null);
      }
    };

    const saveForAllInstancesLoading = ref(false);
    const unlinkSyncElementLoading = ref(false);

    const saveForAllInstances = async () => {
      try {
        saveForAllInstancesLoading.value = true;

        const {
          elementType,
          elementName,
          elementId,
          syncElementId,
          elementPartType,
          elementSubtype,
        } = selectedSyncElement.value;

        const templatePart = await getTemplatePartsBySyncElementIds(idShop, [syncElementId ?? '']);

        if (templatePart.items && templatePart.items.length) {
          const result = await savePartInDatabase(
            idShop,
            elementType,
            elementName ?? '',
            `#${elementId}`,
            asInt(templatePart.items[0].id_template_parts),
            true,
            syncElementId,
            elementPartType,
          );

          if (
            selectedNotUsedTemplates.value.length
            && (elementPartType === ElementPartTypeEnum.STYLES || elementType === TemplateStructureEnum.DESIGN || elementType === TemplateStructureEnum.SECTION)
          ) {
            const resultApply = await securedNestPost(
              'v4',
              '/template/applySyncElementToSelectedTemplates',
              {},
              {
                idShop,
                syncElementId,
                elementType,
                elementPartType,
                elementSubtype,
                selectedNotUsedTemplates: selectedNotUsedTemplates.value,
                selectedSectionsByTemplate: selectedNotUsedTemplatesSections.value,
              },
            );
            if (resultApply && !resultApply.success) {
              throw new Error(t('templateBuilder.saveSyncElementModal.error.saveForAllInstances'));
            }
          }

          // Prepare initRedirect value: comma-separated template IDs or empty string
          const initRedirectValue = selectedNotUsedTemplates.value.length > 0
            ? selectedNotUsedTemplates.value.join(',')
            : '';

          await securedNestPost(
            'v4',
            '/template/updateSyncElements',
            {},
            {
              idShop,
              syncElementId,
              elementType,
              elementId,
              idTemplate: state.template?.id,
              deleted: false,
              initRedirect: initRedirectValue,
            },
          );

          await updateSyncElementsInTemplate({
            elementType,
            elementName: elementName ?? '',
            elementId,
            syncElementId: syncElementId ?? '',
            elementPartType,
            elementSubtype,
            result,
          });

          store.commit('liveEditor/setSelectedSyncElement', null);
          store.commit('liveEditor/setCurrentTemplateState', null);

          await togglePanel('widgetsPanel');
          saveForAllInstancesLoading.value = false;
          displaySaveSyncElementTemplatesModal.value = false;
          
          // Reset selected templates after successful save
          resetNotUsedTemplatesSelection();
          
          showToastSuccess(t('templateBuilder.saveSyncElementModal.success.saveForAllInstances'));
          // If from edit mode
          if (exitModeModalCallback.value) {
            await delay(100);
            await exitModeModalCallback.value();
          }
          setRefreshTemplatesList(true);
        } else {
          saveForAllInstancesLoading.value = false;
          throw new Error(t('templateBuilder.saveSyncElementModal.error.saveForAllInstances'));
        }
      } catch (err) {
        saveForAllInstancesLoading.value = false;
        showToastError(t('templateBuilder.saveSyncElementModal.error.saveForAllInstances'));
      }
    };

    const handleSaveForAllInstances = async () => {
      if (selectedSyncElement.value && selectedSyncElement.value.elementType === TemplateStructureEnum.SECTION && !selectedSyncElement.value.elementPartType) {
        const syncElementInFrame = getElementFromIframe(`#${selectedSyncElement.value.elementId}`);
        if (syncElementInFrame) {
          const syncStyleWidgets = Array.from(syncElementInFrame.querySelectorAll(`[${TEMPLATE_SYNC_ELEMENT_IDENTIFIER}][${TEMPLATE_PART_ELEMENT_PART_TYPE}="styles"]`));
          if (syncStyleWidgets.length > 0) {
            confirm.require({
              group: 'syncedElements',
              header: t('templateBuilder.saveSyncElementModal.confirmSaveWithStylesSync.title'),
              message: t('templateBuilder.saveSyncElementModal.confirmSaveWithStylesSync.message', { count: syncStyleWidgets.length }),
              acceptLabel: t('yes'),
              rejectLabel: t('no'),
              accept: async () => {
                await saveForAllInstances();
              },
            });
            return;
          }
        }
      }
      await saveForAllInstances();
    };

    // Command in edit mode
    const commandSaveForAllInstances = async () => {
      const validation = await informationPanelSettingsValidate(true, selectedSyncElement.value.elementName);
      informationPanelValidation.value = validation.validate;

      if (!validation.success) {
        // Open InformationPanel to see errors
        if (!state.isLeftToolbarVisible || state.leftToolbar.show.informationPanel === false) {
          await togglePanel('informationPanel');
        }
      } else {
        displaySaveSyncElementTemplatesModal.value = true;
      }
    };

    const showUnlinkAndSaveModal = async () => {
      displayUnlinkSyncElementModal.value = true;
    };

    const commandUnlinkAndSave = async () => {
      try {
        if (syncElementUsedTemplatesRef.value && state.template) {
          const selectedRows = syncElementUsedTemplatesRef.value.getSelectedRows() || [];
          const currentTemplate = selectedRows.find((row: any) => row === state.template?.id);
          const openedTemplates = state.templates.filter((templateItem) => selectedRows.includes(templateItem.id)).map((templateItem) => templateItem.id);
          const selectedTemplates = selectedRows.filter((row: any) => row !== state.template?.id).filter((row: any) => !openedTemplates.includes(row));
          if (selectedTemplates.length) {
            const resultUnlink = await securedNestPost(
              'v4',
              '/template/unlinkSyncElementsInTemplates',
              {},
              {
                idShop,
                syncElementId: selectedSyncElement.value.syncElementId,
                selectedTemplates,
              },
            );
            if (resultUnlink && !resultUnlink.success) {
              throw new Error(t('templateBuilder.saveSyncElementModal.error.unlinkAndSave'));
            }
          }
          if (openedTemplates.length) {
            const syncElementData = JSON.stringify({
              [TEMPLATE_SYNC_ELEMENT_IDENTIFIER]: selectedSyncElement.value.syncElementId,
            });
            updateSyncElementsInOpenedTemplates(state?.template?.id ?? 0, { data: syncElementData }, true, openedTemplates);
          }
          if (currentTemplate) {
            unlinkSyncElementLoading.value = true;
            unlinkSyncElement(selectedSyncElement.value);
          }
          removeActiveItemElementDecorations();
          updateSectionsInState(false);
          if (isEmailTemplate(state.template?.type) && selectedSyncElement.value.elementType === TemplateStructureEnum.WIDGET) {
            const selectedSyncElementInIframe = getElementFromIframe(`#${selectedSyncElement.value.elementId}`);
            if (selectedSyncElementInIframe) {
              const sectionParent = getSectionParent(selectedSyncElementInIframe);
              if (sectionParent && sectionParent.hasAttribute(TEMPLATE_SYNC_ELEMENT_IDENTIFIER) && !sectionParent.hasAttribute(TEMPLATE_PART_ELEMENT_PART_TYPE)) {
                await updateParentSection(sectionParent.getAttribute(TEMPLATE_SYNC_ELEMENT_IDENTIFIER) || '', sectionParent.id);
              }
            }
          }
          store.commit('liveEditor/setSelectedSyncElement', null);
          store.commit('liveEditor/setExitEditModeModalVisible', false);
          await togglePanel('widgetsPanel');
          if (exitModeModalCallback.value) {
            await delay(100);
            await exitModeModalCallback.value();
          }
          displayUnlinkSyncElementModal.value = false;
          unlinkSyncElementLoading.value = false;
          showToastSuccess(t('templateBuilder.saveSyncElementModal.success.unlinkAndSave'));
        }
      } catch (error) {
        showToastError(t('templateBuilder.saveSyncElementModal.error.unlinkAndSave'));
      }
    };
    const commandCancelModifications = async () => {
      confirm.require({
        group: 'syncedElements',
        message: t('templateBuilder.confirm.cancelEditSyncElement.message'),
        header: t('templateBuilder.confirm.cancelEditSyncElement.header'),
        icon: 'far fa-exclamation-triangle',
        acceptLabel: t('yes'),
        acceptClass: 'p-button-danger',
        rejectLabel: t('no'),
        rejectClass: 'p-button-secondary',
        accept: async () => {
          store.commit('liveEditor/setExitEditModeModalVisible', false);
          removeActiveItemElementDecorations();
          store.commit('liveEditor/setSelectedSyncElement', null);
          await togglePanel('widgetsPanel');
          restoreTemplateBeforeEdit();
        },
      });
    };

    const saveAndLeaveCommand = async () => {
      store.commit('liveEditor/setIsExitCurrentChangesModalVisible', false);
      const editingTemplateFromCampaign = getEditingTemplateFromCampaign();
      let primaryTemplateToSwitch: any = null;

      if (openingFromCampaign.value && editingTemplateFromCampaign) {
        const templatesLinkedToThisCampaign = state.templatesLinkedToCampaign.filter((c: any) => c.idCampaign === editingTemplateFromCampaign.id_campaign);
        const idTemplatesLinkedToThisCampaign = templatesLinkedToThisCampaign.map((c: any) => c.id);
        const primaryTemplate = editingTemplateFromCampaign.idTemplate;

        // on est dans un template de redirection
        if (idTemplatesLinkedToThisCampaign.includes(state.template?.id) && state.template?.id !== primaryTemplate) {
          primaryTemplateToSwitch = state.templatesLinkedToCampaign.filter((c: any) => c.id === primaryTemplate);
          primaryTemplateToSwitch = primaryTemplateToSwitch.length ? primaryTemplateToSwitch[0] : null;
        }
      } else {
        // vérifier si on est dans un template de redirection
        primaryTemplateToSwitch = state.templates.filter((c: any) => {
          if (c.configuration.nextIdTemplate) {
            const [keyNextIdTemplate, valueNextIdTemplate] = Object.entries(c.configuration.nextIdTemplate)[0];
            const valueNextIdTemplateNumber = valueNextIdTemplate && Array.isArray(valueNextIdTemplate) ? valueNextIdTemplate.map(Number) : [];
            if (valueNextIdTemplateNumber && Array.isArray(valueNextIdTemplateNumber) && valueNextIdTemplateNumber.includes(Number(state.template?.id))) {
              return true;
            }
          }
          return false;
        });
        primaryTemplateToSwitch = primaryTemplateToSwitch.length ? primaryTemplateToSwitch[0] : null;
      }

      if (primaryTemplateToSwitch) {
        await executeTemplateSave(true, false);
        await activate(primaryTemplateToSwitch.id, '', primaryTemplateToSwitch.type ?? '');
      } else {
        await executeTemplateSave(true);
      }
      updateLastRollbackGroupId(getLastPointOfSaveId(historyList.value));
      setRefreshTemplatesList(true);
    };

    const leaveWithoutSavingCommand = async () => {
      confirm.require({
        group: 'leaveWithoutSaveConfirmDialog',
        message: t('templateBuilder.confirm.leaveWithoutSave.message'),
        header: t('templateBuilder.confirm.leaveWithoutSave.header'),
        icon: 'far fa-exclamation-triangle',
        acceptLabel: t('yes'),
        acceptClass: 'p-button-danger',
        rejectLabel: t('no'),
        rejectClass: 'p-button-secondary',
        accept: async () => {
          store.commit('liveEditor/setIsExitCurrentChangesModalVisible', false);
          if (state.testEmailSent) {
            const rollbackPointIndex = getHistoryIndexFromGroupId(historyList.value, state.lastRollbackId);
            startHistoryRestore(1);
            try {
              await restoreToHistoryPoint(historyList.value, rollbackPointIndex, (current, total, actionDetail) => {
                updateHistoryRestoreProgress(current, total, actionDetail);
              });
              refreshSectionsConfig(JSON.parse(historyList.value[rollbackPointIndex].sections_order_after ?? '').map((id: string) => id.toString()));
              refreshTranslations();
              setTemplateConfigurationKeyInState('currentIdTemplateElementsHistoryGroup', +state.lastRollbackId);
              await saveTemplate();
              setTestEmailSent(false);
            } finally {
              endHistoryRestore();
            }
          }

          if (!openingFromCampaign.value) {
            state.templates.map(
              async (s) => {
                deactivate(selectedTemplateId.value ?? 0);
                if (state.hasAny) {
                  await activate(state.templates[state.index].id, '', state.templates[state.index].type);
                  return true;
                }
                return false;
              },
            );
          } else {
            const editingTemplateFromCampaign = getEditingTemplateFromCampaign();
            const idCampaign = editingTemplateFromCampaign?.id_campaign ?? 0;
            if (idCampaign > 0) {
              state.templatesLinkedToCampaign.filter((c: any) => c.idCampaign === idCampaign).map(
                async (s) => {
                  deactivate(selectedTemplateId.value ?? 0);
                  if (state.hasAny) {
                    await activate(state.templates[state.index].id, '', state.templates[state.index].type);
                    return true;
                  }
                  return false;
                },
              );
            }
          }
        },
      });
    };

    const btnSaveComputed: ComputedRef<PrimvueMenuModel> = computed(() => {
      const button = {
        label: t('templateBuilder.save'),
        class: 'p-button-secondary',
        items: [
          {
            label: t('templateBuilder.saveAndLeave'),
            command: saveAndLeaveCommand,
          },
          {
            label: t('templateBuilder.saveAndStay'),
            command: async () => {
              const editingTemplateFromCampaign = getEditingTemplateFromCampaign();

              if (openingFromCampaign.value && editingTemplateFromCampaign) {
                const templatesLinkedToThisCampaign = state.templatesLinkedToCampaign.filter((c: any) => c.idCampaign === editingTemplateFromCampaign.id_campaign);
                const idTemplatesLinkedToThisCampaign = templatesLinkedToThisCampaign.map((c: any) => c.id);
                const primaryTemplate = editingTemplateFromCampaign.idTemplate;

                // on est dans un template de redirection
                if (idTemplatesLinkedToThisCampaign.includes(state.template?.id) && state.template?.id !== primaryTemplate) {
                  await executeTemplateSave(false, false);
                  return true;
                }
              }

              await executeTemplateSave(false);
              updateLastRollbackGroupId(getLastPointOfSaveId(historyList.value));
              return true;
            },
          },
          {
            label: t('templateBuilder.leaveWithoutSaving'),
            command: leaveWithoutSavingCommand,
          },
        ],
      };

      if (!openingFromCampaign.value) {
        button.items.splice(2, 0, {
          label: t('templateBuilder.minimize'),
          command: async () => hideTemplateEditor(),
        });
      }

      if (selectedSyncElement.value) {
        button.items = [
          {
            label: t('templateBuilder.editSyncElementActions.saveForAllInstances'),
            command: commandSaveForAllInstances,
          },
          {
            label: t('templateBuilder.editSyncElementActions.unlinkAndSave'),
            command: showUnlinkAndSaveModal,
          },
          {
            label: t('templateBuilder.editSyncElementActions.previewInstances'),
            command: async () => {
              displaySyncElementTemplatesModal.value = true;
            },
          },
          {
            label: t('templateBuilder.leaveWithoutSaving'),
            command: commandCancelModifications,
          },
          {
            label: t('templateBuilder.editSyncElementActions.help'),
            command: async () => {
              openHelp('164', false);
            },
          },
        ];
      }

      return button;
    });

    const toolbarRightButtons: ComputedRef<PrimvueMenuModel[]> = computed(() => [btnSaveComputed.value]);

    const advancedCssItems = computed(() => {
      const advancedCssMediaQueries = {
        label: t('templateBuilder.toolbar.openCssPanelMediaQueries'),
        class: state.leftToolbar.show.cssPanelMediaQueries ? 'current-active-menu' : '',
        command: async () => {
          await togglePanel('cssPanelMediaQueries');
        },
      };

      const cssItems = [
        {
          label: t('templateBuilder.toolbar.openCssPanel'),
          class: (state.leftToolbar.show.cssPanel && !state.leftToolbar.show.cssPanelMediaQueries) ? 'current-active-menu' : '',
          command: async () => {
            await togglePanel('cssPanel');
          },
        },
      ];

      if (state.template) {
        const design = state.template.sections.find((section) => section.id_template_elements.toString() === state.template?.content.design.toString());
        if (design) {
          const designData = JSON.parse(design.data || '{}');
          if (!designData[HIDE_MOBILE_ATTRIBUTE]) {
            cssItems.push(advancedCssMediaQueries);
          }
        } else {
          cssItems.push(advancedCssMediaQueries);
        }
      } else {
        cssItems.push(advancedCssMediaQueries);
      }

      return cssItems;
    });

    const contentHeaderButtonsLeft = computed(() => ([
      {
        multipleType: 'buttons',
        items: [
          {
            icon: 'far fa-fw fa-info',
            class: () => `${state.leftToolbar.show.informationPanel && 'active'}`,
            tooltip: t('templateBuilder.toolbar.tooltip.information'),
            command: async () => {
              togglePanel('informationPanel', null).then(() => {
                if (selectedSyncElement.value) {
                  store.dispatch('liveEditor/prepareStructureConfiguration', selectedSyncElement.value.structureConfiguration);
                }
              });
            },
          },
          {
            icon: 'far fa-fw fa-cogs',
            class: () => `${state.leftToolbar.show.configurationPanel && 'active'}`,
            tooltip: t('templateBuilder.toolbar.tooltip.configPanel'),
            hide:
            (!state.leftToolbar.show.configurationPanel
              || isSmsTemplate(selectedTemplateType.value)
              || isPushTemplate(selectedTemplateType.value)
            ) && (!selectedSyncElement.value
              || (selectedSyncElement.value && !isPanelVisible(selectedSyncElement.value.elementType, 'configurationPanel', selectedSyncElement.value.elementPartType))
            ),
            command: () => {
              togglePanel('configurationPanel', null).then(() => {
                if (selectedSyncElement.value) {
                  store.dispatch('liveEditor/prepareStructureConfiguration', selectedSyncElement.value.structureConfiguration);
                } else {
                  removeActiveItemElementDecorations();
                  store.commit('liveEditor/resetSelectedStructure');
                }
              });
            },
          },
          {
            icon: 'far fa-fw fa-th-large',
            class: () => `${state.leftToolbar.show.widgetsPanel && 'active'}`,
            tooltip: t('templateBuilder.toolbar.tooltip.insertWidget'),
            command: async () => {
              togglePanel('widgetsPanel', null).then(() => {
                if (selectedSyncElement.value) {
                  store.dispatch('liveEditor/prepareStructureConfiguration', selectedSyncElement.value.structureConfiguration);
                }
              });
            },
            hide: isSmsTemplate(selectedTemplateType.value)
              || isPushTemplate(selectedTemplateType.value)
              || state.template?.informations.imported
              || (selectedSyncElement.value && !isPanelVisible(selectedSyncElement.value.elementType, 'widgetsPanel', selectedSyncElement.value.elementPartType)),
          },
          {
            icon: 'far fa-fw fa-pager',
            class: () => `${state.leftToolbar.show.loadElementsPanel && 'active'}`,
            tooltip: t('templateBuilder.toolbar.tooltip.loadElements'),
            command: () => {
              togglePanel('loadElementsPanel', null).then(() => {
                if (selectedSyncElement.value) {
                  store.dispatch('liveEditor/prepareStructureConfiguration', selectedSyncElement.value.structureConfiguration);
                }
              });
            },
            hide: (!isEmailTemplate(selectedTemplateType.value) && !isDisplayTemplate(selectedTemplateType.value))
              || state.template?.informations.imported
              || (selectedSyncElement.value !== null && !isPanelVisible(selectedSyncElement.value.elementType, 'loadElementsPanel', selectedSyncElement.value.elementPartType)),
          },
          {
            icon: isSmsTemplate(selectedTemplateType.value) || isPushTemplate(selectedTemplateType.value) ? 'far fa-fw fa-th-large' : 'far fa-fw fa-paint-brush',
            tooltip: isSmsTemplate(selectedTemplateType.value) || isPushTemplate(selectedTemplateType.value) ? t('templateBuilder.toolbar.tooltip.configPanel')
              : t('templateBuilder.toolbar.tooltip.designPanel'),
            class: () => `${state.leftToolbar.show.designPanel && 'active'}`,
            command: async () => {
              // If template is not SMS or Push, or if another panel is opened, we let the user click the button
              if ((!isSmsTemplate(selectedTemplateType.value) && !isPushTemplate(selectedTemplateType.value)) || !state.leftToolbar.show.designPanel) {
                if (isEmailTemplate(selectedTemplateType.value)) {
                  getPageConfig();
                }

                await togglePanel('designPanel');
              }
            },
            hide: isFacebookTemplate(selectedTemplateType.value)
              || state.template?.informations.imported
              || (selectedSyncElement.value !== null && !isPanelVisible(selectedSyncElement.value.elementType, 'designPanel', selectedSyncElement.value.elementPartType)),
          },
          {
            icon: 'far fa-fw fa-text',
            class: (): string => `${state.leftToolbar.show.translationPanel && 'active'}`,
            tooltip: t('templateBuilder.toolbar.tooltip.translationPanel'),
            command: async () => {
              togglePanel('translationPanel', null).then(() => {
                if (selectedSyncElement.value) {
                  store.dispatch('liveEditor/prepareStructureConfiguration', selectedSyncElement.value.structureConfiguration);
                }
              });
            },
            hide: selectedSyncElement.value !== null && !isPanelVisible(selectedSyncElement.value.elementType, 'translationPanel', selectedSyncElement.value.elementPartType),
          },
          {
            icon: 'fab fa-fw fa-css3',
            class: () => `${(state.leftToolbar.show.cssPanel || state.leftToolbar.show.cssPanelMediaQueries) && 'active'}`,
            tooltip: t('templateBuilder.toolbar.tooltip.advancedCss'),
            hide: !state.template?.informations.imported
              && ((!isEmailTemplate(selectedTemplateType.value) && !isDisplayTemplate(selectedTemplateType.value)) || !advancedModeEnabled.value),
            items: advancedCssItems.value,
          },
        ],
      },
      {
        multipleType: 'buttons',
        hide: historyList.value.length === 0 || selectedSyncElement.value !== null,
        items: [
          {
            icon: 'far fa-regular fa-clock',
            class: () => `${(state.leftToolbar.show.historyPanel && 'active') || (historyList.value.length === 0 && 'disabled')}`,
            tooltip: t('templateBuilder.toolbar.tooltip.historyPanel'),
            command: async () => {
              if (historyList.value.length !== 0) {
                const result = await getTemplateHistoryGroupListPaginated(0, HISTORY_PAGE_SIZE);
                historyList.value = result.items;
                historyHasMore.value = result.hasMore;
                historyTotalItems.value = result.total;
                await togglePanel('historyPanel');
              }
            },
          },
          {
            icon: 'far fa-fw fa-undo',
            class: () => `${firstHistoryGroupId.value === currentHistoryGroupId.value && 'disabled'}`,
            tooltip: t('templateBuilder.toolbar.tooltip.undo'),
            disabled: () => firstHistoryGroupId.value === currentHistoryGroupId.value,
            command: () => {
              if (firstHistoryGroupId.value !== currentHistoryGroupId.value) {
                handlePreviousHistory();
              }
            },
          },
          {
            icon: 'far fa-fw fa-redo',
            class: () => `${lastHistoryGroupId.value === currentHistoryGroupId.value && 'disabled'}`,
            tooltip: t('templateBuilder.toolbar.tooltip.redo'),
            disabled: () => lastHistoryGroupId.value === currentHistoryGroupId.value,
            command: () => {
              if (lastHistoryGroupId.value !== currentHistoryGroupId.value) {
                handleNextHistory();
              }
            },
          },
        ],
      },
      {
        multipleType: 'buttons',
        hide: selectedSyncElement.value !== null,
        items: [
          {
            icon: 'far fa-fw fa-paper-plane',
            hide: isDisplayTemplate(selectedTemplateType.value) || isFacebookTemplate(selectedTemplateType.value),
            tooltip: t('templateBuilder.toolbar.tooltip.sendTestMessage'),
            command: async () => {
              // Save template before sending a test message
              await executeTemplateSave(false, false, false, HistoryType.INTERMEDIATE_POINT_OF_SAVE);
              // say that the test email has been sent
              setTestEmailSent(true);

              updateLastRollbackGroupId(getLastPointOfSaveId(historyList.value));

              if (isPushTemplate(selectedTemplateType.value)) {
                // If push notification template, we ask for browser authorization and we sent the notification
                sendTestNotification(getTestPushNotificationData());
              } else {
                // Validate InformationPanel values
                const validation = await informationPanelSettingsValidate();
                informationPanelValidation.value = validation.validate;

                if (!validation.success) {
                  // Open InformationPanel to see errors
                  if (!state.isLeftToolbarVisible || state.leftToolbar.show.informationPanel === false) {
                    await togglePanel('informationPanel');
                  }
                } else {
                  // If email or SMS, we open the modal
                  store.commit('general/showSendTestModal');
                }
              }
            },
          },
          {
            icon: 'far fa-cog',
            tooltip: t('templateBuilder.toolbar.tooltip.advancedCss'),
            hide: isSmsTemplate(selectedTemplateType.value) || isPushTemplate(selectedTemplateType.value) || isFacebookTemplate(selectedTemplateType.value)
              || state.template?.informations.imported,
            items: [
              {
                label: t('templateBuilder.toolbar.toggleAdvancedMode'),
                icon: !state.advancedModeEnabled ? 'far fa-square' : 'far fa-check-square',
                iconPos: 'left',
                command: () => {
                  toggleAdvancedMode();
                },
              },
            ],
          },
        ],
      },
      {
        multipleType: 'buttons',
        hide: isSmsTemplate(selectedTemplateType.value) || isPushTemplate(selectedTemplateType.value) || isFacebookTemplate(selectedTemplateType.value),
        items: [
          {
            icon: 'far fa-fw fa-desktop',
            class: () => `${width.value === TemplateWidth.DESKTOP && 'active'}`,
            tooltip: t('templateBuilder.toolbar.tooltip.desktopView'),
            command: () => { width.value = TemplateWidth.DESKTOP; },
          },
          {
            icon: 'far fa-fw fa-tablet',
            class: () => `${width.value === TemplateWidth.TABLET && 'active'}`,
            tooltip: t('templateBuilder.toolbar.tooltip.tabletView'),
            command: () => { width.value = TemplateWidth.TABLET; },
          },
          {
            icon: 'far fa-fw fa-mobile',
            class: () => `${width.value === TemplateWidth.MOBILE && 'active'}`,
            tooltip: t('templateBuilder.toolbar.tooltip.mobileView'),
            command: () => { width.value = TemplateWidth.MOBILE; },
          },
        ],
      },
    ]));

    const closeDuplicationModal = () => {
      showDuplicationModal.value = false;
    };

    const duplicateConfirmationDialog = (newName: string) => {
      showDuplicationModal.value = false;
      duplicateTemplate(idShop, selectedTemplateId.value, null, null, newName).then((result) => {
        if (result.success && result.id) {
          showToastSuccess(t('templates.confirm.duplicate.success.message'));
          updateSectionsInState(false);
          activate(result.id, '', selectedTemplateType.value);
          setRefreshTemplatesList(true);
        } else {
          showToastError(t('templates.confirm.duplicate.error.message'));
        }
      });
    };

    const changeTab = (s: any) => {
      restoreTemplateBeforeEdit();
      updateSectionsInState(false);
      activate(s.id, s.informations.name, s.type);
    };

    const tabs: ComputedRef<PrimvueMenuModel[]> = computed(() => {
      if (selectedSyncElement.value) {
        return [];
      }
      let buttons: PrimvueMenuModel[] = [];
      if (!openingFromCampaign.value) {
        buttons = state.templates.map(
          (s) => {
            const isActiveTemplate = (s.id === selectedTemplateId.value);

            return {
              label: s.informations.name,
              class: `${isActiveTemplate ? 'p-button-primary' : 'p-button-secondary'} footer-btn`,
              command: !isActiveTemplate ? () => { changeTab(s); } : undefined,
              items: [
                {
                  label: t('templateBuilder.rename'),
                  command: () => {
                    valueRenameTemplateModal.value = s.informations.name;
                    displayRenameTemplateModal.value = true;
                  },
                },
                {
                  label: t('templateBuilder.duplicate'),
                  command: () => {
                    newListName.value = t('myLists.manage.actions.cloneText') + s.informations.name;
                    showDuplicationModal.value = true;
                  },
                },
                {
                  label: t('templateBuilder.close'),
                  command: () => {
                    if (state.template?.edited) {
                      store.commit('liveEditor/setIsExitCurrentChangesModalVisible', true);
                    } else {
                      deactivate(selectedTemplateId.value ?? 0);
                      if (state.hasAny) {
                        activate(state.templates[state.index].id, '', state.templates[state.index].type);
                      }
                    }
                  },
                },
              ],
            };
          },
        );
        buttons.push({
          icon: 'far fa-plus',
          class: 'create-new-btn p-button-primary footer-btn',
          command: () => {
            showTemplateSelector.value = true;
          },
        });
      } else {
        const editingTemplateFromCampaign = getEditingTemplateFromCampaign();
        const idCampaign = editingTemplateFromCampaign?.id_campaign ?? 0;
        if (idCampaign > 0) {
          buttons = state.templatesLinkedToCampaign.filter((c: any) => c.idCampaign === idCampaign).map(
            (s) => {
              const isActiveTemplate = (s.id === selectedTemplateId.value);

              return {
                label: s.informations.name,
                class: `${isActiveTemplate ? 'p-button-primary' : 'p-button-secondary'} footer-btn`,
                command: !isActiveTemplate ? () => { updateSectionsInState(false); activate(s.id, s.informations.name, s.type); } : undefined,
                items: [
                  {
                    label: t('templateBuilder.rename'),
                    command: () => {
                      valueRenameTemplateModal.value = s.informations.name;
                      displayRenameTemplateModal.value = true;
                    },
                  },
                  {
                    label: t('templateBuilder.duplicate'),
                    command: () => {
                      newListName.value = t('myLists.manage.actions.cloneText') + s.informations.name;
                      showDuplicationModal.value = true;
                    },
                  },
                  {
                    label: t('templateBuilder.close'),
                    command: () => {
                      if (state.template?.edited) {
                        store.commit('liveEditor/setIsExitCurrentChangesModalVisible', true);
                      } else {
                        deactivate(selectedTemplateId.value ?? 0);
                        if (state.hasAny) {
                          activate(state.templates[state.index].id, '', state.templates[state.index].type);
                        }
                      }
                    },
                  },
                ],
              };
            },
          );
        }
      }
      return buttons;
    });

    const stopWatchers: any[] = [];

    stopWatchers.push(watch(() => selectedStructure.value, (values) => {
      if (values && values.type !== TemplateStructureEnum.PAGE && !isConfigurationPanelVisible.value) {
        showConfigurationPanel();
      }

      if (values && values.type !== TemplateStructureEnum.PAGE && !isLeftToolbarVisible.value) {
        togglePanel('configurationPanel');
      }
    }));

    stopWatchers.push(watch(() => state.leftToolbar.show, () => {
      if (state.leftToolbar.show.designPanel) { // If we show designPanel, prepare structure configuration
        prepareStructureConfiguration();
      }
    }, { deep: true }));

    stopWatchers.push(watch(() => state.leftToolbar.show.translationPanel, () => {
      if (!state.leftToolbar.show.translationPanel) {
        refreshAttributeInDOM(locale.value);

        // Restore original code of all smart lists
        restoreSmartListsOriginalCode();

        const defaultLanguage = getShopDefaultLang();
        updateIframeTranslations(state.template?.translations.filter((translation) => translation.language === defaultLanguage) ?? []);

        resetNeedToTranslate();

        // Store original code of lists
        refreshSmartProductsListsOriginalStructure();

        // Recalculate smart list with default language
        refreshSmartProductList();

        resetListeners();
      } else {
        // Restore original code of all smart lists
        restoreSmartListsOriginalCode();
        removeUnusedTranslations();
        fixMissingTranslations();
        sortTranslationInState();
        // Store original code of lists
        refreshSmartProductsListsOriginalStructure();

        // Recalculate smart list with default language
        refreshSmartProductList();
      }
    }));

    stopWatchers.push(watch(lastHistoryGroupId, async (newValue) => {
      historyList.value = await getTemplateHistoryGroupList();
      setLastHistory(newValue);
    }));

    watch(() => syncElementWarning, () => {
      if (syncElementWarning.value) {
        showEditSyncElementWarning(
          confirm,
          t,
          syncElementWarning.value.elementType,
          syncElementWarning.value.acceptCallback,
          syncElementWarning.value.rejectCallback,
          syncElementWarning.value.additionalMessage ?? '',
          syncElementWarning.value.overrideMessage ?? '',
          syncElementWarning.value.elementPartType,
        );
      }
    }, { deep: true });

    onBeforeMount(async () => {
      historyList.value = await getTemplateHistoryGroupList();

      updateLastRollbackGroupId(getLastPointOfSaveId(historyList.value));
      if (!state.template?.configuration.currentIdTemplateElementsHistoryGroup) {
        // In case we open a template directly from database, we set the last point of save as the current history point
        const lastPointOfSave = getClosestPointOfSave(historyList.value);
        const lastHistoryPointOfSave: TemplatesElementsHistoryGroup = historyList.value[lastPointOfSave];

        if (lastHistoryPointOfSave) {
          setTemplateConfigurationKeyInState('currentIdTemplateElementsHistoryGroup', lastHistoryPointOfSave.id_template_elements_history_group);
        }
      }
      setLastHistory(currentHistoryGroupId.value);

      if (historyList.value.length === 0) {
        // Create first point of save of template if history list is empty
        await createHistory(HistoryType.POINT_OF_SAVE);
      }
    });

    // Surveiller les conflits de version détectés
    watch(
      () => state.versionConflicts,
      async (conflicts) => {
        if (conflicts && conflicts.length > 0) {
          // Traiter chaque conflit détecté
          for (const conflict of conflicts) {
            await new Promise<void>((resolve) => {
              // Stocker les données du conflit dans les refs
              currentConflictMessage.value = String(t('templateBuilder.confirm.versionConflict.message', { templateName: conflict.templateName }));
              currentConflictLocalDate.value = conflict.localDate;
              currentConflictRemoteDate.value = conflict.remoteDate;
              
              confirm.require({
                group: 'versionConflict',
                message: currentConflictMessage.value,
                header: t('templateBuilder.confirm.versionConflict.header'),
                icon: 'far fa-exclamation-triangle',
                acceptLabel: t('templateBuilder.confirm.versionConflict.loadRecent'),
                acceptClass: 'p-button-primary',
                rejectLabel: t('templateBuilder.confirm.versionConflict.keepLocal'),
                rejectClass: 'p-button-secondary',
                accept: async () => {
                  // L'utilisateur choisit de charger depuis la BDD
                  setPreventDateModificationUpdate(false);
                  await reloadTemplateFromDatabase(conflict.templateId);
                  resolve();
                },
                reject: () => {
                  // L'utilisateur choisit de continuer avec ses modifications
                  setPreventDateModificationUpdate(false);
                  resolve();
                },
              });
            });
          }
          
          // Réinitialiser les conflits après traitement
          clearVersionConflicts();
          // Réinitialiser le flag de prévention (au cas où l'utilisateur aurait fermé le dialog sans choisir)
          setPreventDateModificationUpdate(false);
        }
      },
      { deep: true },
    );

    onMounted(() => {
      window.addEventListener('unload', saveTemplatesInLocalStorage);
    });

    onBeforeUnmount(() => {
      store.commit('liveEditor/setSelectedSyncElement', null);
      store.commit('liveEditor/destroySortableInstances');
      saveTemplatesInLocalStorage();
      window.removeEventListener('unload', saveTemplatesInLocalStorage);
      destroyAllSortables();
      stopWatchers.forEach((stop) => stop());

      // Stop scrolling widgets listener
      stopScrolling();

      document.removeEventListener('dragover', trackMousePosition);
    });

    const closeTemplateSelector = () => {
      showTemplateSelector.value = false;
    };

    const selectTemplateFromTemplateSelector = async (idTemplate: number, label: string, type: string) => {
      activate(idTemplate, label, type).then(() => {
        showTemplateSelector.value = false;
        store.commit('general/hideTheSpinner');
      });
    };

    /**
     * Execute functions after iframe is loaded
     */
    const iframeLoaded = async (done: Function) => {
      // Skip panel switching if we're restoring history (preserve current panel)
      if (!isRestoringHistory.value) {
        if (isSmsTemplate(selectedTemplateType.value) || isPushTemplate(selectedTemplateType.value)) {
          await togglePanel('designPanel');
          prepareStructureConfiguration();
        } else if (state.template?.informations.imported) {
          await togglePanel('translationPanel');
        } else if (isEmailTemplate(selectedTemplateType.value) && !state.template?.informations.imported) {
          await togglePanel('widgetsPanel');
        }
      } else {
        // Reset the flag after iframe reload
        isRestoringHistory.value = false;
      }
      clearPlaceholder();
      await updateUsedSyncElements(idShop);
      refreshSmartProductsListsOriginalStructure();
      done();
      if (isDisplayTemplate(selectedTemplateType.value)) {
        const design = state.template ? state.template.sections[0] : null;
        if (state.leftToolbar.show.designPanel) {
          prepareStructureConfiguration();
          store.commit('liveEditor/refreshConfigurationPanelKey');
        }
        if (design) {
          const data = JSON.parse(design.data || '{}');
          if (data[TEMPLATE_SYNC_ELEMENT_IDENTIFIER] && data[TEMPLATE_SYNC_ELEMENT_NAME]) {
            const template = getTemplateIframeDocument();
            if (!template) return;

            const body = template.querySelector('#spm_body');
            if (!body) return;

            body.setAttribute(TEMPLATE_SYNC_ELEMENT_IDENTIFIER, data[TEMPLATE_SYNC_ELEMENT_IDENTIFIER]);
            body.setAttribute(TEMPLATE_SYNC_ELEMENT_NAME, data[TEMPLATE_SYNC_ELEMENT_NAME]);
          }
        }
      }
      removeActiveItemElementDecorations();
      if (exitModeModalCallback.value) {
        await exitModeModalCallback.value();
      }
    };

    const handleInsertElement = () => {
      livePreviewKey.value += 1;
    };

    const handleSyncElementUsedTemplatesLoaded = (total: number) => {
      syncElementUsedTemplatesTotal.value = total;
      syncElementUsedTemplatesLoaded.value = true;
    };

    const handleSyncElementNotUsedTemplatesLoaded = (total: number) => {
      syncElementNotUsedTemplatesTotal.value = total;
    };

    const handleNotUsedTemplatesSelectionChanged = (data: { selectedIds: number[]; selectedSectionsByTemplate: Record<number, string[]> }) => {
      selectedNotUsedTemplates.value = data.selectedIds;
      selectedNotUsedTemplatesSections.value = data.selectedSectionsByTemplate;
    };

    const resetNotUsedTemplatesSelection = () => {
      selectedNotUsedTemplates.value = [];
      selectedNotUsedTemplatesSections.value = {};
      // Reset selection in the component as well
      if (syncElementNotUsedTemplatesRef.value?.resetSelection) {
        syncElementNotUsedTemplatesRef.value.resetSelection();
      }
    };

    const handleSaveSyncElementTemplatesModalHide = () => {
      syncElementUsedTemplatesLoaded.value = false;
      resetNotUsedTemplatesSelection();
    };

    const handleUnlinkSyncElementModalHide = () => {
      syncElementUsedTemplatesLoaded.value = false;
    };

    const shouldShowNotUsedTemplates = computed(() => {
      if (!selectedSyncElement.value) return false;

      const { elementType, elementPartType, elementSubtype } = selectedSyncElement.value;

      // Show only if has elementPartType and elementType is section or widget or if element sync is design
      if (!elementPartType && elementType === TemplateStructureEnum.DESIGN) return true;

      if (elementType === TemplateStructureEnum.SECTION) return true;

      if (elementPartType === ElementPartTypeEnum.STYLES && elementSubtype && getWidgetType(elementSubtype) !== WidgetTypeEnum.IMAGE) return true;

      return false;
    });

    const getSyncElementUsedTemplatesNumberTitle = (unlink: boolean) => {
      if (!selectedSyncElement.value) return '';

      const { elementPartType, elementType, elementId } = selectedSyncElement.value;

      if (elementPartType === ElementPartTypeEnum.STYLES) {
        return t(
          'templateBuilder.saveSyncElementModal.templateNumber',
          {
            element: t('templateBuilder.saveSyncElementModal.styleSync'),
            count: syncElementUsedTemplatesTotal.value,
          },
        );
      }

      if (elementType === TemplateStructureEnum.SECTION && !unlink) {
        const syncElementInFrame = getElementFromIframe(`#${elementId}`);
        if (syncElementInFrame) {
          const syncWidgets = Array.from(syncElementInFrame.querySelectorAll(`[${TEMPLATE_SYNC_ELEMENT_IDENTIFIER}]`));
          if (syncWidgets.length > 0) {
            return t(
              'templateBuilder.saveSyncElementModal.templateNumberWithChildSync',
              {
                element: t('templateBuilder.saveSyncElementModal.elementSync'),
                count: syncElementUsedTemplatesTotal.value,
                childElements: t('templateBuilder.saveSyncElementModal.childSyncElementsNumber', { count: syncWidgets.length }),
              },
            );
          }
        }
      }

      return t(
        'templateBuilder.saveSyncElementModal.templateNumber',
        {
          element: t('templateBuilder.saveSyncElementModal.elementSync'),
          count: syncElementUsedTemplatesTotal.value,
        },
      );
    };

    const getSyncElementUsedTemplatesNumberTitleWhenSaving = (unlink: boolean) => {
      const label = unlink ? 'templateBuilder.saveSyncElementModal.erase.unlinkLabel' : 'templateBuilder.saveSyncElementModal.erase.syncLabel';
      return t(
        label,
        {
          count: syncElementUsedTemplatesTotal.value,
        },
      );
    };

    const syncElementUsedTemplateListLabel = computed(() => {
      if (!selectedSyncElement.value) return '';

      const { elementPartType } = selectedSyncElement.value;

      if (elementPartType === ElementPartTypeEnum.STYLES) {
        return t(
          'templateBuilder.saveSyncElementModal.templateListLabel',
          {
            element: t('templateBuilder.saveSyncElementModal.styleSync').toLowerCase(),
          },
        );
      }

      return t(
        'templateBuilder.saveSyncElementModal.templateListLabel',
        {
          element: t('templateBuilder.saveSyncElementModal.elementSync').toLowerCase(),
        },
      );
    });

    const syncElementUsedTemplateListLabelForUnlink = computed(() => {
      if (!selectedSyncElement.value) return '';

      const { elementPartType } = selectedSyncElement.value;

      if (elementPartType === ElementPartTypeEnum.STYLES) {
        return t(
          'templateBuilder.saveSyncElementModal.templateListLabelForUnlink',
          {
            element: t('templateBuilder.saveSyncElementModal.styleSync').toLowerCase(),
          },
        );
      }

      return t(
        'templateBuilder.saveSyncElementModal.templateListLabelForUnlink',
        {
          element: t('templateBuilder.saveSyncElementModal.elementSync').toLowerCase(),
        },
      );
    });

    const syncElementNotUsedTemplatesDescription = computed(() => {
      if (!selectedSyncElement.value) return '';

      const { elementPartType } = selectedSyncElement.value;
      if (elementPartType === ElementPartTypeEnum.STYLES) {
        return t(
          'templateBuilder.saveSyncElementModal.notUsedTemplatesDescription',
          {
            element: t('templateBuilder.saveSyncElementModal.styleSync').toLowerCase(),
          },
        );
      }
      return t(
        'templateBuilder.saveSyncElementModal.notUsedTemplatesDescription',
        {
          element: t('templateBuilder.saveSyncElementModal.designSync').toLowerCase(),
        },
      );
    });

    const hideExitEditModeModal = () => {
      store.commit('liveEditor/setExitEditModeModalVisible', false);
      store.commit('liveEditor/setExitEditModeCallback', null);
    };

    const isExitCurrentChangesModalVisible = computed(() => store.getters['liveEditor/getIsExitCurrentChangesModalVisible']);

    const hideExitCurrentChangeModal = () => {
      store.commit('liveEditor/setIsExitCurrentChangesModalVisible', false);
    };

    const confirmationModal = computed(() => store.getters['liveEditor/getConfirmationModal']);

    watch(confirmationModal, () => {
      if (confirmationModal.value) {
        confirm.require({
          group: 'confirmationModal',
          message: confirmationModal.value.message,
          header: confirmationModal.value.title,
          icon: 'far fa-exclamation-triangle',
          acceptLabel: t('yes'),
          acceptClass: 'p-button-danger',
          rejectLabel: t('no'),
          rejectClass: 'p-button-secondary',
          accept: async () => {
            await confirmationModal.value.acceptCallback();
          },
          reject: async () => {
            await confirmationModal.value.rejectCallback();
          },
        });
      }
    }, { deep: true });

    return {
      width,
      selectedTemplateId,
      selectedTemplateName,
      selectedTemplateType,
      selectedStructure,
      selectedStructureActions,
      state,
      currentConflictMessage,
      currentConflictLocalDate,
      currentConflictRemoteDate,
      isLeftToolbarVisible,
      toolbarRightButtons,
      contentHeaderButtonsLeft,
      tabs,
      livePreviewKey,
      historyList,
      selectedHistory,
      displayRenameTemplateModal,
      valueRenameTemplateModal,
      displaySaveElementModal,
      typeSaveElementModal,
      nameSaveElementModal,
      syncStateSaveElementModal,
      saveElementModalRef,
      displaySearchElementsModal,
      searchElementsType,
      searchElementsPartType,
      searchElementsSubtype,
      includeAllElementsPartType,
      displaySyncElementTemplatesModal,
      rename,
      handleChangeHistory,
      handleLoadMoreHistory,
      historyHasMore,
      historyIsLoadingMore,
      historyTotalItems,
      saveElement,
      handleSaveElementClose,
      handleSearchElements,
      handleSearchElementsClose,
      handleSearchElementSelected,
      sendTestTemplate,
      showTemplateSelector,
      closeTemplateSelector,
      selectTemplateFromTemplateSelector,
      iframeLoaded,
      isConfigurationPanelClosable,
      informationPanelValidation,
      newListName,
      t,
      showDuplicationModal,
      closeDuplicationModal,
      duplicateConfirmationDialog,
      isChooseZipFileModalVisible,
      isWidgetPanelClosable,
      isTranslationPanelClosable,
      generateSha1Hash,
      MEDIA_URL_PART_THUMB_GENERATION,
      toEraseElementId,
      subtitle,
      idShop,
      displayElementsModal,
      handleInsertElement,
      displaySaveSyncElementTemplatesModal,
      saveForAllInstancesLoading,
      selectedSyncElement,
      handleSyncElementUsedTemplatesLoaded,
      syncElementUsedTemplatesLoaded,
      handleSyncElementNotUsedTemplatesLoaded,
      handleNotUsedTemplatesSelectionChanged,
      handleSaveSyncElementTemplatesModalHide,
      syncElementNotUsedTemplatesRef,
      shouldShowNotUsedTemplates,
      exitModeModalVisible,
      hideExitEditModeModal,
      commandSaveForAllInstances,
      commandUnlinkAndSave,
      commandCancelModifications,
      isExitCurrentChangesModalVisible,
      hideExitCurrentChangeModal,
      saveAndLeaveCommand,
      leaveWithoutSavingCommand,
      isSendTestModalVisible,
      getSyncElementUsedTemplatesNumberTitle,
      syncElementUsedTemplateListLabel,
      syncElementNotUsedTemplatesDescription,
      displayUnlinkSyncElementModal,
      handleUnlinkSyncElementModalHide,
      unlinkSyncElementLoading,
      showUnlinkAndSaveModal,
      syncElementUsedTemplateListLabelForUnlink,
      syncElementUsedTemplatesRef,
      handleSaveForAllInstances,
      toEraseElementSyncId,
      getSyncElementUsedTemplatesNumberTitleWhenSaving,
      toEraseIsNewSync,
    };
  },
});
</script>

<style scoped lang="scss">
.language-selector {
  background-color: $white;
  border: 1px solid $heather;
  border-radius: 2px;
  color: $shuttle-grey;

  &:hover {
    border-color: $heather !important;
  }

  &:not(.p-disabled).p-focus {
    box-shadow: none;
    border-color: $heather !important;
  }
}

.close-sidebar-button {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 1;
}
</style>

<style  lang="scss">
.create-new-btn {
  width: 1.5rem !important;
  height: 1.5rem !important;
}
.p-dialog-content {
  .to-erase-section {
    border: $template-builder-panels-sections-section-border;
    width: 100%;
    height: 200px;
    background-size: 100% auto;
  }
}
</style>

<style lang="scss">
.exit-edit-mode-modal-divider {
  border-top: 1px solid #dee2e6;
  margin: 0.5rem 0 !important;
}
.sync-elements-help-link {
  font-size: 0.9rem;
}

.sync-element-divider {
  border-top: 1px solid #dee2e6;
  margin: 1.5rem 0;
}
.configuration-wrapper .p-panel {
  height: 100vh;           /* plein écran */
  display: flex;
  flex-direction: column;
  overflow: auto;          /* c'est CE conteneur qui scroll */
}
.configuration-wrapper .p-panel-header {
  position: -webkit-sticky;
  position: sticky;
  top: 0;
  z-index: 7;
}

/* Limiter la largeur du dialog de conflit de version uniquement */
.version-conflict-dialog-content {
  max-width: 700px;
}
</style>
